import React, { useRef, useState, useEffect, useCallback, useMemo, useContext } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  ButtonGroup,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Search, Refresh, AddCircleOutline, HighlightOff, ErrorOutline, RemoveCircleOutline } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getElementStatus } from "../../utils/schedule.js";
import {
  getReportsTableApi,
  getLatestReportsDetailsApi,
  saveEditApi,
  submitReportApi,
  updateReportStatusApi,
  uploadLocalImageApi,
  getReportImagesApi,
  getRejectHistoryApi,
  uploadAppendixPDFApi,
  getReportAppendixPDFApi,
  isReportLockedApi,
  lockReportApi,
  unlockReportApi,
} from "../../api/report.js";
import { getReportStatusApi } from "../../api/predefine.js";
import { getEarnValueApi } from "../../api/cost.js";
import { useParams } from "react-router-dom";
import { useUser } from "../../hook/useUser.js";
import { useProject } from "../../hook/useProject.js";
import { toLocal } from "../../utils/date";
import { getElementsDataApi, getMainModelDataApi, getBackgroundModelUrlApi } from "../../api/model";
import { getTasksDataApi } from "../../api/schedule.js";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import MainTable from "../../component/table/MainTable.js";
import Container from "../../component/Container.js";
import SelectComponent from "../../component/SelectComponent.js";
import DatePicker from "../../component/DatePicker.js";
import RichTextEditor from "../../component/RichTextEditor.js";
import Popup from "../../component/popup/Popup.js";
import SitePhotoPage from "../SitePhotoPage/index.js";
import LineBarChart from "../../component/chart/LineBarChart";
import AddButtonWithLines from "./components/AddButtonWithLines.js";
import UploadPhoto from "./components/UploadPhoto.js";
import AddTextField from "./components/AddTextField.js";
import RoleBaseActionButtons from "./components/RoleBaseActionButtons.js";
import UploadPDF from "./components/UploadPDF.js";
import SafetyChartTable from "../SafetyPage/components/SafetyChartTable.js";
import TopBottomViewer from "../../component/bim/TopBottomViewer.js";
import ScheduleGantt from "../SchedulePage/components/ScheduleGantt.js";
import FullScreenLoading from "../../component/FullScreenLoading.js";
import TooltipInfo from "../../component/TooltipInfo.js";
import NotificationPopup from "../../component/popup/NotificationPopup.js";

const SafetyPerformanceTable = ({ safetyTableData, onCountChange, readonly }) => {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ px: 2, py: 0.5, bgcolor: "#dbe0f3" }}>Safety Performance</TableCell>
            <TableCell sx={{ px: 2, py: 0.5, bgcolor: "#dbe0f3" }}>Count</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {safetyTableData.map((item) => (
            <TableRow key={item.id}>
              <TableCell sx={{ px: 2, py: 0 }}>{item.Safety}</TableCell>
              <TableCell sx={{ px: 2, py: 0.5 }}>
                <TextField
                  disabled={readonly}
                  type="number"
                  variant="standard"
                  value={item.Count}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue === "" || parseFloat(newValue) >= 0) {
                      onCountChange(item.id, newValue);
                    }
                  }}
                  inputProps={{ style: { fontSize: 11, padding: 0 } }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ReportTextTitle = ({ content, sx }) => {
  return <Typography sx={{ fontSize: "18px", fontWeight: 600, my: 1, ...sx }}>{content}</Typography>;
};

const AddFreeEditorItem = ({ item, index, onTitleChange, onContentChange, onRemove, Appendix = false, readonly }) => {
  return (
    <Box sx={{ position: "relative" }}>
      {Appendix && <ReportTextTitle content={`Appendix ${index + 3}`} sx={{ mb: 0 }} />}
      <TextField
        disabled={readonly}
        fullWidth
        variant="standard"
        value={item.title}
        onChange={(e) => onTitleChange(item.id, e.target.value)}
        sx={{
          mt: 0.5,
          mb: readonly ? 0.5 : 1.5,
          "& .MuiInputBase-input": {
            fontSize: Appendix ? "12px !important" : "18px !important",
            fontWeight: 600,
            color: "#474747",
            py: 0.5,
          },
          "& .Mui-disabled": {
            WebkitTextFillColor: "#474747",
            borderBottomStyle: "none",
          },
        }}
      />
      <RichTextEditor
        key={item.id}
        id={item.id}
        initialValue={item.content}
        onChange={(id, value) => onContentChange(id, value)}
        readonly={readonly}
      />
      {!readonly && (
        <IconButton
          onClick={() => onRemove(item.id)}
          sx={{
            position: "absolute",
            right: 0,
            top: Appendix ? "24px" : "4px",
            color: "#FF0000",
          }}
        >
          <HighlightOff />
        </IconButton>
      )}
    </Box>
  );
};

const ReportSummaryGrid = ({ reportName, projectName, company, manhourSummary, equipmentSummary, clickedDate }) => {
  return (
    <Grid container>
      <Grid item xs={3}>
        Report Name
      </Grid>
      <Grid item xs={9}>
        {reportName}
      </Grid>
      <Grid item xs={3} pt={0.3}>
        Title of Project
      </Grid>
      <Grid item xs={9} pt={0.3}>
        {projectName}
      </Grid>
      <Grid item xs={3} pt={0.3}>
        Date
      </Grid>
      <Grid item xs={9} pt={0.3}>
        {clickedDate}
      </Grid>
      <Grid item xs={3} pt={0.3}>
        Contractor Name
      </Grid>
      <Grid item xs={9} pt={0.3}>
        {company}
      </Grid>
      <Grid item xs={3} pt={0.3}>
        Manhour Summary
      </Grid>
      <Grid item xs={9} pt={0.3}>
        {manhourSummary.map((item, index) => (
          <Typography key={index}>{`${item.category} ${item.value}`}</Typography>
        ))}
      </Grid>
      <Grid item xs={3} pt={0.3}>
        Equipment Summary
      </Grid>
      <Grid item xs={9} pt={0.3}>
        {equipmentSummary.map((item, index) => (
          <Typography key={index}>
            {item.category} {item.value}
          </Typography>
        ))}
      </Grid>
    </Grid>
  );
};

const ReportPage = () => {
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const location = useLocation();
  const { user } = useUser(projectId);
  const project = useProject(projectId);
  const userName = `${user?.firstName || ""} ${user?.lastName || ""}`;
  const company = user?.company || "";
  const userId = user?.userId || "";
  const userRole = user?.userRole || "";
  const projectName = project?.projectName || "";
  const userWithCompany = `${userName} (${company})`;
  const topBottomViewer = useRef();
  const LineChartRef = useRef();
  const ganttChartRef = useRef();
  const [state, setState] = useState({
    modelURL: [],
    backgroundValue: [],
    backgroundModels: [],
    taskTableData: [],
    startDate: dayjs(),
    endDate: dayjs(),
    currentDate: dayjs(),
    selectedReportId: [],
    selectedRowData: [],
    reportTableData: {},
    reportTableHeaders: [
      { id: "projectReportId", label: "Report ID", align: "left" },
      { id: "date", label: "Report Date", align: "left", type: "date" },
      {
        id: "preparedBy",
        label: "Prepared By",
        align: "left",
        defaultSortOrder: "asc",
      },
      {
        id: "lastUpdatedDate",
        label: "Last Updated",
        align: "left",
        type: "dateWithTime",
      },
      { id: "lastUpdatedBy", label: "Last Updated By", align: "left" },
      { id: "status", label: "Status", align: "left" },
    ],
    rejectReasonTableData: [],
    rejectReasonTableHeaders: [
      { id: "datetime", label: "Time", align: "left", type: "dateWithTime" },
      { id: "rejectBy", label: "Rejected/Retracted By", align: "left" },
      { id: "reason", label: "Reason", align: "left" },
    ],
    isLoadingTable: false,
    isLoadingReportDetail: false,
    isLoadingFullScreen: false,
    activeTab: "Daily Report",
    reportTitle: "Report Preview",
    reportName: null,
    rejectPopupName: null,
    rejectPopup: false,
    retractPopup: false,
    rejectReasonPopup: false,
    sitePhotoPopup: false,
    layoutStyle: false,
    isConfirming: false,
    confirmPopupText: null,
    confirmPopupTitle: null,
    rejectionReason: "",
    retractReason: "",
    readonly: false,
    currentEditUser: "",
    currentLockStatus: false,
    ganttChartHeight: true,
    page: 0,
    rowsPerPage: 30,
    selectedGanttRowId: "",
  });

  const [monthlyReportState, setMonthlyReportState] = useState({
    clickedMonthlyDate: "",
    clickedMonthlyDateFormat: "",
    ManhourSummary: [],
    EquipmentSummary: [],
    TRIRValue: "0",
    LTIRValue: "0",
    PotentialAllValue: "0",
    PotentialCurrentValue: "0",
    ActualValue: "0",
    otherHSSEFreeEditor: "<p>No Remarks</p>",
    mainFreeEditorItems: [
      {
        id: "Procurement and Fabrication",
        title: "Procurement and Fabrication",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Design and Engineering",
        title: "Design and Engineering",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Authorizations, Regulatory and Permitting",
        title: "Authorizations, Regulatory and Permitting",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Site Construction Works",
        title: "Site Construction Works",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Testing and Commissioning",
        title: "Testing and Commissioning",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Quality Assurance and Quality Control",
        title: "Quality Assurance and Quality Control",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Schedule and Progress",
        title: "Schedule and Progress",
        content: "<p>No Remarks</p>",
      },
    ],
    costFinancialsFreeEditor: "<p>No Remarks</p>",
    contractManageFreeEditor: "<p>No Remarks</p>",
    worksPlanFreeEditor: "<p>No Remarks</p>",
    anyOtherMatterFreeEditor: "<p>No Remarks</p>",
    newFreeEditorItems: [],
    uploadedImages: [],
    uploadedPDF: null,
    newAppendixItems: [],
    currentModelImg: null,
    planModelImg: null,
    costChartImg: null,
    ganttChartImg: null,
    signedby: "",
    readonly: false,
    prepareByCompany: null,
  });

  const [weeklyReportState, setWeeklyReportState] = useState({
    clickedWeeklyEndDate: "",
    clickedWeeklyStartDate: "",
    clickedWeeklyDateFormat: "",
    ManhourSummary: [],
    EquipmentSummary: [],
    TRIRValue: "0",
    LTIRValue: "0",
    PotentialAllValue: "0",
    PotentialCurrentValue: "0",
    ActualValue: "0",
    otherHSSEFreeEditor: "<p>No Remarks</p>",
    mainFreeEditorItems: [
      {
        id: "Procurement and Fabrication",
        title: "Procurement and Fabrication",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Design and Engineering",
        title: "Design and Engineering",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Authorizations, Regulatory and Permitting",
        title: "Authorizations, Regulatory and Permitting",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Site Construction Works",
        title: "Site Construction Works",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Testing and Commissioning",
        title: "Testing and Commissioning",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Quality Assurance and Quality Control",
        title: "Quality Assurance and Quality Control",
        content: "<p>No Remarks</p>",
      },
      {
        id: "Schedule and Progress",
        title: "Schedule and Progress",
        content: "<p>No Remarks</p>",
      },
    ],
    worksPlanFreeEditor: "<p>No Remarks</p>",
    anyOtherMatterFreeEditor: "<p>No Remarks</p>",
    newFreeEditorItems: [],
    uploadedImages: [],
    uploadedPDF: null,
    newAppendixItems: [],
    currentModelImg: null,
    planModelImg: null,
    ganttChartImg: null,
    safetyHeadDate: [],
    signedby: "",
    readonly: false,
    prepareByCompany: null,
  });

  const [dailyReportState, setDailyReportState] = useState({
    clickedDailyDate: "",
    clickedDailyDateFormat: "",
    temperatureStart: "",
    temperatureEnd: "",
    weatherValue: [],
    weatherWarningOptions: [
      { label: "Strong Wind Signal No. 3", value: "Strong Wind Signal No. 3" },
      { label: "Storm Signal No. 8", value: "Storm Signal No. 8" },
      { label: "Hurricane Signal No. 10", value: "Hurricane Signal No. 10" },
      {
        label: "Amber Rainstorm Warning Signal",
        value: "Amber Rainstorm Warning Signal",
      },
      {
        label: "Red Rainstorm Warning Signal",
        value: "Red Rainstorm Warning Signal",
      },
      {
        label: "Black Rainstorm Warning Signal",
        value: "Black Rainstorm Warning Signal",
      },
      { label: "Thunderstorm Warning", value: "Thunderstorm Warning" },
      { label: "Very Hot Weather Warning", value: "Very Hot Weather Warning" },
    ],
    weatherWarningValue: [{ category: "", value: "" }],
    ManhourArray: [{ category: "", value: "" }],
    EquipmentArray: [{ category: "", value: "" }],
    safetyTableData: [
      { id: 1, Safety: "Fatal Injury", Count: 0 },
      { id: 2, Safety: "Days Away from Works(LTI)", Count: 0 },
      { id: 3, Safety: "Restricted Work Injury", Count: 0 },
      { id: 4, Safety: "Medical Treatment", Count: 0 },
      { id: 5, Safety: "First Aid Treatment", Count: 0 },
      { id: 6, Safety: "Near Miss", Count: 0 },
    ],
    mainFreeEditorItems: [
      {
        id: 0,
        title: "Other HSSE Remarks",
        content: "<p>No Remarks</p>",
      },
      {
        id: 1,
        title: "Works Completed Today",
        content: "<p>No Remarks</p>",
      },
      {
        id: 2,
        title: "Works Planned for Next Day",
        content: "<p>No Remarks</p>",
      },
      {
        id: 3,
        title: "Any Other Matter or Concerns",
        content: "<p>No Remarks</p>",
      },
    ],
    newFreeEditorItems: [],
    uploadedImages: [],
    safetyHeadDate: [],
    signedby: "",
    readonly: false,
    prepareByCompany: null,
  });

  const [chartData, setChartData] = useState([]);

  const iconColumns = [
    {
      label: "",
      note: "View Latest Rejection/Retraction Reason",
      image: "assets/icons/viewfile.svg",
      action: async (row, event) => {
        if (event) {
          event.stopPropagation();
        }
        if (row) {
          const result = await getRejectHistoryApi(projectId, row.id);
          const rejectReasonTableData = [];
          if (result && result.data) {
            result.data.forEach((item) => {
              rejectReasonTableData.push({
                id: item.id,
                datetime: item.datetime,
                reason: item.reason,
                rejectBy: item.rejectBy.firstName + " " + item.rejectBy.lastName + " (" + item.rejectBy.company + ")",
              });
            });
            setState((prevState) => ({
              ...prevState,
              rejectReasonTableData: rejectReasonTableData,
              rejectReasonPopup: true,
              rejectPopupName: `${state.activeTab} ${row.projectReportId} - ${toLocal(row.date).format("YYYYMMDD")} - ${row.preparedBy}`,
            }));
          }
        }
      },
    },
    {
      label: "",
      note: "Download report.pdf",
      image: "assets/icons/download.svg",
      shouldDisplay: (row) => {
        return [3, 4].includes(row.statusNumber);
      },
    },
  ];

  const reportTab = ["Daily Report", "Weekly Report", "Monthly Report"];

  const GBGTableDataTemplate = [
    { label: "Fatal", headerDate1: "0", headerDate0: "0" },
    { label: "Days Away from Works", headerDate1: "0", headerDate0: "0" },
    { label: "Restricted Work", headerDate1: "0", headerDate0: "0" },
    { label: "Medical Treatment", headerDate1: "0", headerDate0: "0" },
    { label: "First Aid Treatment", headerDate1: "0", headerDate0: "0" },
    { label: "Near Miss", headerDate1: "0", headerDate0: "0" },
    {
      label: "Safety Reporting & Observation",
      headerDate1: "0",
      headerDate0: "0",
    },
  ];

  const treeTableHeaders = [
    { label: "Task ID", width: "100px" },
    { label: "Task Name", width: "170px" },
  ];

  const [reportFilterState, setReportFilterState] = useState({
    startDate: null,
    endDate: null,
  });

  const [elementIds, setElementIds] = useState({
    planProgress: [],
    actualProgress: [],
    delayed: [],
    planComplete: [],
    actualComplete: [],
    showFilter: [],
    white: [],
    all: [],
  });

  const colorRulesData = (data) => {
    const elementStatus = getElementStatus(data, state.currentDate);
    let allIds = data?.map((item) => item?.bimElementId);
    setElementIds((prevState) => ({
      ...prevState,
      ...elementStatus,
      all: allIds,
    }));
  };

  //get main model url and workset
  const getMainModel = async () => {
    const result = await getMainModelDataApi(projectId);
    const data = result?.data;

    if (data && Array.isArray(data)) {
      const mainModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: true,
        };
      });
      setState((prevState) => ({
        ...prevState,
        modelURL: mainModels,
      }));
      await getElementsData();
    }
  };

  //get background model url&filter
  const getBackgroundModelUrl = async () => {
    const result = await getBackgroundModelUrlApi(projectId);
    const data = result?.data;
    if (data && Array.isArray(data)) {
      const backgroundModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: true,
        };
      });
      const backgroundArray = backgroundModels.map((item) => ({
        value: item.fileURL,
        label: item.filename,
      }));
      setState((prevState) => ({
        ...prevState,
        backgroundModels: backgroundModels,
        backgroundValue: backgroundArray.map((b) => b.value),
      }));
    }
  };

  const getElementsData = async () => {
    try {
      const result = await getElementsDataApi(projectId);
      const data = result?.data;
      if (!data) showError("No element data returned from API.");

      setState((prevState) => {
        const newState = {
          ...prevState,
          elementsData: data,
        };
        colorRulesData(newState.elementsData);
        return newState;
      });
    } catch {
      showError("Received element data occurred error.");
    }
  };

  const getTaskData = async () => {
    const result = await getTasksDataApi(projectId);
    const data = result?.data;
    if (!data || !Array.isArray(data)) {
      showError("Invalid or empty task data received.");
      return;
    }
    const minDate = data.reduce((min, task) => {
      const planStart = dayjs(task.planStart);
      const actualStart = task.actualStart ? dayjs(task.actualStart) : null;
      const start = actualStart ? (actualStart.isBefore(planStart) ? actualStart : planStart) : planStart;
      return start.isBefore(min) ? start : min;
    }, dayjs("2999-12-31"));

    const maxDate = data.reduce((max, task) => {
      const planEnd = dayjs(task.planEnd);
      const actualEnd = task.actualEnd ? dayjs(task.actualEnd) : null;
      const end = actualEnd ? (actualEnd.isAfter(planEnd) ? actualEnd : planEnd) : planEnd;
      return end.isAfter(max) ? end : max;
    }, dayjs("1000-01-01"));
    setState((prevState) => ({
      ...prevState,
      startDate: minDate,
      endDate: maxDate,
      taskTableData: data,
    }));
  };

  const handleTabClick = useCallback(
    async (tab) => {
      if (state.selectedReportId.length > 0) {
        await unlockReportApi(projectId, state.selectedReportId[0]); // Unlock the previous report
      }
      setState((prevState) => ({
        ...prevState,
        activeTab: tab,
        selectedReportId: [],
        reportTitle: "Report Preview",
        reportName: null,
        currentEditUser: null,
        currentLockStatus: false,
        page: 0,
        rowsPerPage: 30,
      }));
    },
    [projectId, state]
  );

  const latestState = useRef(state);

  useEffect(() => {
    latestState.current = state;
  }, [state]);

  const unlockCurrentReport = async () => {
    const currentState = latestState.current;
    if (currentState.selectedReportId.length > 0) {
      await unlockReportApi(projectId, currentState.selectedReportId[0]);
    }
  };

  useEffect(() => {
    return () => {
      unlockCurrentReport(); // Unlock the report when leaving the current route
    };
  }, [location.pathname]);

  // Listen for page refresh or close
  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentState = latestState.current;
      if (currentState.selectedReportId.length > 0) {
        unlockReportApi(projectId, currentState.selectedReportId[0]).catch((error) => {
          showError("Error unlocking report on beforeunload:", error);
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [projectId]);

  const handleDateChange = useCallback((field, date) => {
    setReportFilterState((prevState) => ({ ...prevState, [field]: date }));
  }, []);

  const handleSearchClick = () => {
    setState((prevState) => ({ ...prevState, page: 0 }));
    const { startDate, endDate } = reportFilterState;
    getReportTableData(startDate, endDate);
  };

  const handleReset = () => {
    setState((prevState) => ({ ...prevState, page: 0 }));
    setReportFilterState({
      startDate: null,
      endDate: null,
    });
    getReportTableData(null, null);
  };

  const updateGBGTableData = (tableData, result, timePeriod) => {
    const fieldIdMapping = {
      Fatal: {
        all: "[Fatal_Value_All]",
        week: "[Fatal_Value_Week]",
        month: "[Fatal_Value_Month]",
      },
      "Days Away from Works": {
        all: "[Days_Away_from_Works_Value_All]",
        week: "[Days_Away_from_Works_Value_Week]",
        month: "[Days_Away_from_Works_Value_Month]",
      },
      "Restricted Work": {
        all: "[Restricted_Work_Value_All]",
        week: "[Restricted_Work_Value_Week]",
        month: "[Restricted_Work_Value_Month]",
      },
      "Medical Treatment": {
        all: "[Medical_Treatment_Value_All]",
        week: "[Medical_Treatment_Value_Week]",
        month: "[Medical_Treatment_Value_Month]",
      },
      "First Aid Treatment": {
        all: "[First_Aid_Treatment_Value_All]",
        week: "[First_Aid_Treatment_Value_Week]",
        month: "[First_Aid_Treatment_Value_Month]",
      },
      "Near Miss": {
        all: "[Near_Miss_Value_All]",
        week: "[Near_Miss_Value_Week]",
        month: "[Near_Miss_Value_Month]",
      },
      "Safety Reporting & Observation": {
        all: "[Observation_Value_All]",
        week: "[Observation_Value_Week]",
        month: "[Observation_Value_Month]",
      },
    };

    return tableData.map((row) => {
      const fieldIds = fieldIdMapping[row.label];

      if (!fieldIds) {
        return row;
      }
      const selectedFieldId = fieldIds[timePeriod];
      const allValue = findField(result, fieldIds.all, row.headerDate1);
      const periodValue = findField(result, selectedFieldId, row.headerDate0);

      return {
        ...row,
        headerDate1: allValue || "0",
        headerDate0: periodValue || "0",
      };
    });
  };

  const findField = (result, fieldId, defaultValue = "") => result.find((item) => item.fieldId === fieldId)?.fieldValue || defaultValue;

  const createArrayFromFields = (categoriesStr, valuesStr) => {
    const categories = (categoriesStr || "").split(";").map((cat) => cat.trim());
    const values = (valuesStr || "").split(";").map((val) => val.trim());

    if (categories.length === 0 && values.length === 0) {
      return [{ category: "", value: "" }];
    }
    const maxLength = Math.max(categories.length, values.length);
    const result = [];
    for (let i = 0; i < maxLength; i++) {
      result.push({
        category: categories[i] ?? "",
        value: values[i] ?? "",
      });
    }
    return result;
  };

  const handleRowSelect = useCallback(
    async (id) => {
      const clickedRow = state.reportTableData[state.activeTab].find((row) => row.id === id[0]);
      if (clickedRow && state.selectedReportId[0] !== id[0]) {
        let lockedStatus = false;
        let currentEditUser = "";
        // Step 1: Unlock the previous report if it was locked
        if (state.selectedReportId.length > 0) {
          await unlockReportApi(projectId, state.selectedReportId[0]); // Unlock the previous report
        }
        // Step 2: Check if the report is locked
        const lockCheckResult = await isReportLockedApi(projectId, id);
        if (lockCheckResult?.data) {
          lockedStatus = lockCheckResult.data.locked;
          currentEditUser = lockCheckResult.data.locked ? lockCheckResult.data.openedBy : "";
        }
        // Step 3: If not locked, lock it
        if (!lockedStatus) {
          await lockReportApi(projectId, id); // Lock the report
        }
        setState((prevState) => ({
          ...prevState,
          isLoadingFullScreen: true,
        }));

        try {
          const response = await getLatestReportsDetailsApi(projectId, id); //get clicked report details
          if (response) {
            const result = response.data;

            const clickedDailyDate = findField(result, "[YYYY/MM/DD]") || "0000/00/00";
            const clickedMonthlyDate = findField(result, "[YYYY/MM]") || "0000/00";
            const clickedWeeklyStartDate = findField(result, "[From (YYYY/MM/DD)]") || "0000/00/00";
            const clickedWeeklyEndDate = findField(result, "[To (YYYY/MM/DD)]") || "0000/00/00";

            setState((prevState) => {
              let reportTitle = "";
              let reportName = "";
              if (state.activeTab === "Weekly Report") {
                reportTitle = `${prevState.activeTab} ${clickedWeeklyStartDate} - ${clickedWeeklyEndDate}`;
                reportName = `${prevState.activeTab} ${clickedRow.projectReportId || ""} - ${clickedWeeklyStartDate} - ${clickedWeeklyEndDate} - ${
                  clickedRow.preparedBy
                }`;
              } else if (state.activeTab === "Monthly Report") {
                reportTitle = `${prevState.activeTab} ${clickedMonthlyDate}`;
                reportName = `${prevState.activeTab} ${clickedRow.projectReportId || ""} - ${clickedMonthlyDate} - ${clickedRow.preparedBy}`;
              } else if (state.activeTab === "Daily Report") {
                reportTitle = `${prevState.activeTab} ${clickedDailyDate}`;
                reportName = `${prevState.activeTab} ${clickedRow.projectReportId || ""} - ${clickedDailyDate} - ${clickedRow.preparedBy}`;
              } else {
                reportTitle = `${prevState.activeTab}`;
                reportName = `${prevState.activeTab} - ${clickedRow.preparedBy}`;
              }
              return {
                ...prevState,
                selectedReportId: id,
                selectedRowData: clickedRow,
                reportTitle: reportTitle,
                reportName: reportName,
                rejectionReason: "",
                retractReason: "",
                readonly: clickedRow.shouldBeReadonly || lockedStatus,
                currentEditUser: currentEditUser,
                currentLockStatus: lockedStatus,
              };
            });

            const manhourCategoryStr = findField(result, "[Manhour Category (Summary)]")
              .split("; ")
              .map((item) => item.trim());
            const manhourValueStr = findField(result, "[Manhour Value (Summary)]")
              .split("; ")
              .map((item) => item.trim());
            const ManhourSummary = manhourCategoryStr.map((item, index) => ({
              id: index,
              category: item,
              value: manhourValueStr[index],
            }));
            const equipmentCategoryStr = findField(result, "[Equipment Category (Summary)]")
              .split("; ")
              .map((item) => item.trim());
            const equipmentValueStr = findField(result, "[Equipment Value (Summary)]")
              .split("; ")
              .map((item) => item.trim());
            const EquipmentSummary = equipmentCategoryStr.map((item, index) => ({
              id: index,
              category: item,
              value: equipmentValueStr[index],
            }));
            const imageIdsStr = findField(result, "{Upload from Project site photo library | Upload local file}");
            const planModelStr = findField(result, "{img1}");
            const currentModelStr = findField(result, "{img2}");
            const ganttChartStr = findField(result, "{project gantt}");
            const costChartStr = findField(result, "{img4}");
            const pdfIdStr = findField(result, "[upload file from local device]");
            const uploadedImages = [];

            const imagesResult = await getReportImagesApi(projectId, id); //get clicked report image
            const planModelUpload =
              imagesResult?.data.find((item) => item.id === planModelStr)?.fileURL;
            const currentUpload =
              imagesResult?.data.find((item) => item.id === currentModelStr)?.fileURL;
            const ganttChartUpload =
              imagesResult?.data.find((item) => item.id === ganttChartStr)?.fileURL;
            const costChartUpload =
              imagesResult?.data.find((item) => item.id === costChartStr)?.fileURL;

            const newFreeSectionNameStr = findField(result, "(New Free Section Name)");
            const newFreeSectionContentStr = findField(result, "(New Free Section Content)");
            let newFreeEditorItems = [];
            if (newFreeSectionNameStr && newFreeSectionContentStr) {
              const names = newFreeSectionNameStr.split("; ").map((item) => item.trim());
              const contents = newFreeSectionContentStr.split("; ").map((item) => item.trim());
              newFreeEditorItems = names.map((title, index) => ({
                id: index,
                title: title,
                content: contents[index],
                isEditable: false,
              }));
            }
            const newAppendixNameStr = findField(result, "(New Free Appendix Name)");
            const newAppendixContentStr = findField(result, "(New Free Appendix Content)");

            let newAppendixItems = [];
            if (newAppendixNameStr && newAppendixContentStr) {
              const names = newAppendixNameStr.split("; ").map((item) => item.trim());
              const contents = newAppendixContentStr.split("; ").map((item) => item.trim());
              newAppendixItems = names.map((title, index) => ({
                id: index,
                title: title,
                content: contents[index],
                isEditable: false,
              }));
            }

            const appendixPDFResult = await getReportAppendixPDFApi(projectId, id); //get clicked report appendix pdf
            const appendixPDFUpload = appendixPDFResult?.data.find((item) => item.id.toString() === pdfIdStr);

            if (imageIdsStr) {
              const imageIds = imageIdsStr.split("; ").map((id) => id.trim());
              imageIds.forEach((imageId) => {
                const imageData = imagesResult?.data.find((item) => item.id === imageId);
                if (imageData) {
                  uploadedImages.push({
                    id: imageData.id,
                    templateId: imageData.id,
                    name: imageData.filename,
                    url: imageData.fileURL,
                    date: imageData.uploadDatetime,
                    uploadedBy: `${imageData.uploadBy.firstName} ${imageData.uploadBy.lastName} (${imageData.uploadBy.company})`,
                  });
                }
              });
            }

            if (state.activeTab === "Daily Report") {
              const safetyFieldsMapping = {
                "Fatal Injury": "[Fatal_Value]",
                "Days Away from Works(LTI)": "[Days_Away_from_Works_Value]",
                "Restricted Work Injury": "[Restricted_Work_Value]",
                "Medical Treatment": "[Medical_Treatment_Value]",
                "First Aid Treatment": "[First_Aid_Treatment_Value]",
                "Near Miss": "[Near_Miss_Value]",
              };
              const newCounts = Object.fromEntries(
                Object.entries(safetyFieldsMapping).map(([category, fieldId]) => [
                  category,
                  result.find((item) => item.fieldId === fieldId)?.fieldValue || "0",
                ])
              );
              const freeTextMapping = {
                "[Free text Other HSSE Remarks]": "Other HSSE Remarks",
                "[Free text Works Completed Today]": "Works Completed Today",
                "[Free text Works Planned for Next Day]": "Works Planned for Next Day",
                "[Free text Any Other Matter or Concerns]": "Any Other Matter or Concerns",
              };
              const newContents = Object.fromEntries(
                Object.entries(freeTextMapping).map(([fieldId, title]) => [
                  title,
                  result.find((item) => item.fieldId === fieldId)?.fieldValue || "<p>No Remarks</p>",
                ])
              );
              const weatherWarningValueField = findField(result, "[Weather Warning (Value)]");
              const weatherWarningTimeField = findField(result, "[Weather Warning (Time)]");
              const manhourCategoryField = findField(result, "[Manhour_Category (Free text)]");
              const manhourValueField = findField(result, "[Manhour_Value (Numeric)]");
              const equipmentCategoryField = findField(result, "[Equipment_Category (Free text)]");
              const equipmentValueField = findField(result, "[Equipment_Value (Numeric)]");
              const updatedWeatherWarningArray = createArrayFromFields(weatherWarningValueField, weatherWarningTimeField);
              const updatedManhourArray = createArrayFromFields(manhourCategoryField, manhourValueField);
              const updatedEquipmentArray = createArrayFromFields(equipmentCategoryField, equipmentValueField);
              setDailyReportState((prevState) => ({
                ...prevState,
                safetyTableData: prevState.safetyTableData.map((item) => {
                  return {
                    ...item,
                    Count: newCounts[item.Safety] !== undefined ? newCounts[item.Safety] : item.Count,
                  };
                }),
                temperatureStart: findField(result, "[Temperature_Value_1 (Numeric)]"),
                temperatureEnd: findField(result, "[Temperature_Value_2 (Numeric)]"),
                weatherValue: findField(result, "[Weather (Free text)]")
                  ?.split("; ")
                  ?.map((item) => item.trim())
                  ?.filter((item) => item.length > 0),
                weatherWarningValue: updatedWeatherWarningArray,
                ManhourArray: updatedManhourArray,
                EquipmentArray: updatedEquipmentArray,
                mainFreeEditorItems: prevState.mainFreeEditorItems.map((item) => ({
                  ...item,
                  content: newContents[item.title] || item.content,
                })),
                newFreeEditorItems: newFreeEditorItems,
                uploadedImages: uploadedImages,
                signedby: findField(result, "[Signed by]"),
                clickedDailyDate: clickedDailyDate,
                clickedDailyDateFormat: findField(result, "[Date]") || "",
                prepareByCompany: clickedRow.preparedBy,
              }));
            } else if (state.activeTab === "Weekly Report") {
              const freeTextMapping = {
                "[Free text Procurement and Fabrication]": "Procurement and Fabrication",
                "[Free text Design and Engineering]": "Design and Engineering",
                "[Free text Authorizations, Regulatory and Permitting]": "Authorizations, Regulatory and Permitting",
                "[Free text Site Construction Works]": "Site Construction Works",
                "[Free text Testing and Commissioning]": "Testing and Commissioning",
                "[Free text Quality Assurance and Quality Control]": "Quality Assurance and Quality Control",
                "[Free text Schedule and Progress]": "Schedule and Progress",
              };
              const newContents = Object.fromEntries(
                Object.entries(freeTextMapping).map(([fieldId, title]) => [
                  title,
                  result.find((item) => item.fieldId === fieldId)?.fieldValue || "<p>No Remarks</p>",
                ])
              );
              const GBGTableData = updateGBGTableData(GBGTableDataTemplate, result, "week");
              setWeeklyReportState((prevState) => ({
                ...prevState,
                ManhourSummary: ManhourSummary,
                EquipmentSummary: EquipmentSummary,
                GBGTableData: GBGTableData,
                TRIRValue: findField(result, "[TRIR_Value]") || "0",
                LTIRValue: findField(result, "[LTIR_Value]") || "0",
                PotentialAllValue: findField(result, "[SIF_Potential_Value_All]") || "0",
                PotentialCurrentValue: findField(result, "[SIF_Potential_Value_Current]") || "0",
                ActualValue: findField(result, "[SIF_Actual_Value_All]") || "0",
                otherHSSEFreeEditor: findField(result, "[Free text Other HSSE Remarks]"),
                worksPlanFreeEditor: findField(result, "[Free text Works Planned for Next Week]"),
                anyOtherMatterFreeEditor: findField(result, "[Free text Any Other Matter or Concerns]"),
                mainFreeEditorItems: prevState.mainFreeEditorItems.map((item) => ({
                  ...item,
                  content: newContents[item.title] || item.content,
                })),
                newFreeEditorItems: newFreeEditorItems,
                uploadedImages: uploadedImages,
                uploadedPDF: appendixPDFUpload,
                newAppendixItems: newAppendixItems,
                currentModelImg: currentUpload,
                planModelImg: planModelUpload,
                ganttChartImg: ganttChartUpload,
                safetyHeadDate: [`${clickedWeeklyStartDate}-${clickedWeeklyEndDate}`, "Project to Date"],
                signedby: findField(result, "[Signed by]"),
                clickedWeeklyEndDate: clickedWeeklyEndDate,
                clickedWeeklyStartDate: clickedWeeklyStartDate,
                clickedWeeklyDateFormat: findField(result, "[Date]") || "",
                prepareByCompany: clickedRow.preparedBy,
              }));
            } else if (state.activeTab === "Monthly Report") {
              const freeTextMapping = {
                "[Free text Procurement and Fabrication]": "Procurement and Fabrication",
                "[Free text Design and Engineering]": "Design and Engineering",
                "[Free text Authorizations, Regulatory and Permitting]": "Authorizations, Regulatory and Permitting",
                "[Free text Site Construction Works]": "Site Construction Works",
                "[Free text Testing and Commissioning]": "Testing and Commissioning",
                "[Free text Quality Assurance and Quality Control]": "Quality Assurance and Quality Control",
                "[Free text Schedule and Progress]": "Schedule and Progress",
              };
              const newContents = Object.fromEntries(
                Object.entries(freeTextMapping).map(([fieldId, title]) => [
                  title,
                  result.find((item) => item.fieldId === fieldId)?.fieldValue || "<p>No Remarks</p>",
                ])
              );

              const GBGTableData = updateGBGTableData(GBGTableDataTemplate, result, "month");
              setMonthlyReportState((prevState) => ({
                ...prevState,
                ManhourSummary: ManhourSummary,
                EquipmentSummary: EquipmentSummary,
                GBGTableData: GBGTableData,
                TRIRValue: findField(result, "[TRIR_Value]") || "0",
                LTIRValue: findField(result, "[LTIR_Value]") || "0",
                PotentialAllValue: findField(result, "[SIF_Potential_Value_All]") || "0",
                PotentialCurrentValue: findField(result, "[SIF_Potential_Value_Current]") || "0",
                ActualValue: findField(result, "[SIF_Actual_Value_All]") || "0",
                otherHSSEFreeEditor: findField(result, "[Free text Other HSSE Remarks]"),
                costFinancialsFreeEditor: findField(result, "[Free text Cost and Financials]"),
                contractManageFreeEditor: findField(result, "[Free text Contract and Change Management]"),
                worksPlanFreeEditor: findField(result, "[Free text Works Planned for Next Month]"),
                anyOtherMatterFreeEditor: findField(result, "[Free text Any Other Matter or Concerns]"),
                mainFreeEditorItems: prevState.mainFreeEditorItems.map((item) => ({
                  ...item,
                  content: newContents[item.title] || item.content,
                })),
                newFreeEditorItems: newFreeEditorItems,
                uploadedImages: uploadedImages,
                uploadedPDF: appendixPDFUpload,
                newAppendixItems: newAppendixItems,
                currentModelImg: currentUpload,
                planModelImg: planModelUpload,
                costChartImg: costChartUpload,
                ganttChartImg: ganttChartUpload,
                safetyHeadDate: [dayjs(clickedMonthlyDate).format("MMM YYYY"), "Project to Date"],
                signedby: findField(result, "[Signed by]"),
                clickedMonthlyDate: clickedMonthlyDate,
                clickedMonthlyDateFormat: findField(result, "[Date]") || "",
                prepareByCompany: clickedRow.preparedBy,
              }));
            }
            setTimeout(() => {
              setState((prevState) => ({
                ...prevState,
                isLoadingFullScreen: false,
              }));
            }, 2000);
          } else {
            showError("Received data occurred error.");
          }
        } catch {
          showError("Received data occurred error.");
        }
      }
    },
    [state.activeTab, state.reportTableData, state.selectedReportId]
  );

  const changeSafetyTable = (id, value) => {
    setDailyReportState((prevState) => ({
      ...prevState,
      safetyTableData: prevState.safetyTableData.map((item) => (item.id === id ? { ...item, Count: value } : item)),
    }));
  };

  const manhourChange = (newTextFieldsArray) => {
    setDailyReportState((prevState) => ({
      ...prevState,
      ManhourArray: newTextFieldsArray,
    }));
  };

  const equipmentChange = (newTextFieldsArray) => {
    setDailyReportState((prevState) => ({
      ...prevState,
      EquipmentArray: newTextFieldsArray,
    }));
  };

  const useAddNewFreeEditor = (setParamState, stateKey) => {
    const addNewEditor = useCallback(() => {
      setParamState((prevState) => ({
        ...prevState,
        [stateKey]: [
          ...prevState[stateKey],
          {
            // id: prevState[stateKey].length,
            id: uuidv4(),
            title: `New Free Section Name...`,
            content: "<p>Type something here...</p>",
            isEditable: true,
          },
        ],
      }));
    }, [setParamState, stateKey]);
    return addNewEditor;
  };

  const addNewDailyFreeEditor = useAddNewFreeEditor(setDailyReportState, "newFreeEditorItems");
  const addNewWeeklyFreeEditor = useAddNewFreeEditor(setWeeklyReportState, "newFreeEditorItems");
  const addNewMonthlyFreeEditor = useAddNewFreeEditor(setMonthlyReportState, "newFreeEditorItems");
  const addNewWeeklyAppendix = useAddNewFreeEditor(setWeeklyReportState, "newAppendixItems");
  const addNewMonthlyAppendix = useAddNewFreeEditor(setMonthlyReportState, "newAppendixItems");

  const useHandleImagesChange = (setParamState, stateKey, uploadedBy, reportId) => {
    const handleImagesChange = useCallback(
      async (event) => {
        const files = event.target.files;
        if (files && files.length > 0) {
          try {
            for (const file of Array.from(files)) {
              const formData = new FormData();
              formData.append("file", file);
              const result = await uploadLocalImageApi(projectId, reportId, formData);

              if (result) {
                const photoId = result.data.reportImageId;
                const newImage = {
                  url: URL.createObjectURL(file),
                  name: file.name,
                  uploadedBy: uploadedBy,
                  date: new Date().toLocaleString(),
                  id: photoId,
                  templateId: "r" + photoId,
                };

                setParamState((prevState) => ({
                  ...prevState,
                  [stateKey]: [...prevState[stateKey], newImage],
                }));
              }
            }
          } catch (error) {
            showError(error);
          }
        }
      },
      [setParamState, stateKey, uploadedBy, reportId]
    );
    return handleImagesChange;
  };

  const dailyImagesChange = useHandleImagesChange(setDailyReportState, "uploadedImages", userWithCompany, state.selectedReportId);
  const weeklyImagesChange = useHandleImagesChange(setWeeklyReportState, "uploadedImages", userWithCompany, state.selectedReportId);
  const monthlyImagesChange = useHandleImagesChange(setMonthlyReportState, "uploadedImages", userWithCompany, state.selectedReportId);

  const imgFromSitePhoto = (value) => {
    const newImages = Array.from(value).map((item) => ({
      url: item.image,
      name: item.name,
      uploadedBy: item.uploadedBy,
      date: item.date,
      id: item.id,
      templateId: "s" + item.id,
    }));
    setState((prevState) => ({
      ...prevState,
      sitePhotoPopup: false,
    }));
    Promise.all(
      [setDailyReportState, setWeeklyReportState, setMonthlyReportState].map((setState) =>
        setState((prevState) => ({
          ...prevState,
          uploadedImages: [...prevState.uploadedImages, ...newImages],
        }))
      )
    );
  };

  // const calculateReadonly = (statusNumber) => {
  //   const isPM = user?.isPM;
  //   return (
  //     (["System Administrator", "Executive User", "CLP User"].includes(userRole) && statusNumber === 4) ||
  //     (userRole === "Contractor" &&
  //       (([3, 4].includes(statusNumber) && !isPM) ||
  //         // statusNumber 2,isn't PM - readonly
  //         (statusNumber === 2 && !isPM)))
  //   );
  // };

  const calculateReadonly = (statusNumber, preparedBy) => {
    const isPM = user?.isPM;
    // userRole是CLP User,打开contractor的report,readonly
    if (userRole === "CLP User" && preparedBy !== "CLP") {
      return true;
    }
    // System Administrator / Executive User 在 statusNumber === 4 时readonly
    if (["System Administrator", "Executive User"].includes(userRole)) {
      return statusNumber === 4;
    }
    // Contractor 的情况：status 2/3/4 且不是 PM 时readonly
    if (userRole === "Contractor") {
      return [2, 3, 4].includes(statusNumber) && !isPM;
    }
    return false;
  };

  const getReportTableData = useCallback(
    async (startDate, endDate) => {
      setState((prevState) => ({
        ...prevState,
        isLoadingTable: true,
      }));
      try {
        const result1 = await getReportsTableApi(projectId, 1);
        const result2 = await getReportsTableApi(projectId, 2);
        const result3 = await getReportsTableApi(projectId, 3);

        const statusResult = await getReportStatusApi();
        let statusMap = {};
        if (statusResult && statusResult.data) {
          statusResult.data.forEach((item) => {
            let label = item.label;
            statusMap[item.value] = label;
          });
        }
        const transformData = (data) => {
          return data?.map((item) => ({
            ...item,
            status: statusMap[item.status] || item.status,
            statusNumber: item.status,
            lastUpdatedBy: item.firstName + " " + item.lastName,
            shouldBeReadonly: calculateReadonly(item.status, item.preparedBy),
          }));
        };

        const transformedResult3 = transformData(result3?.data);
        const transformedResult2 = transformData(result2?.data);
        const transformedResult1 = transformData(result1?.data);
        const filterByDateRange = (data, startDate, endDate) => {
          if (!startDate && !endDate) {
            return data;
          }

          return data.filter((item) => {
            const reportDate = toLocal(dayjs(item.date));
            let start = null;
            let end = null;
            if (startDate) {
              start = toLocal(dayjs(startDate)).startOf("day");
            }
            if (endDate) {
              end = toLocal(dayjs(endDate)).endOf("day");
            }
            if (start && end) {
              return reportDate.isBetween(start, end, "day", "[]");
            } else if (start) {
              return reportDate.isSameOrAfter(start);
            } else if (end) {
              return reportDate.isSameOrBefore(end);
            }
            return true;
          });
        };

        const filteredResult3 = filterByDateRange(transformedResult3, startDate, endDate);
        const filteredResult2 = filterByDateRange(transformedResult2, startDate, endDate);
        const filteredResult1 = filterByDateRange(transformedResult1, startDate, endDate);

        const newReportTableData = {
          "Daily Report": filteredResult3,
          "Weekly Report": filteredResult2,
          "Monthly Report": filteredResult1,
        };
        const updatedSelectedRowData =
          state.selectedRowData && newReportTableData[state.activeTab]?.find((row) => row.id === state.selectedRowData.id);
        setState((prevState) => ({
          ...prevState,
          reportTableData: newReportTableData,
          selectedRowData: updatedSelectedRowData || prevState.selectedRowData,
          readonly: updatedSelectedRowData?.shouldBeReadonly || false,
          isLoadingTable: false,
        }));
      } catch {
        showError("Received report data occurred error.");
      }
    },
    [projectId, state.activeTab, state.selectedRowData, userRole]
  );

  const getCostData = async () => {
    const response = await getEarnValueApi(projectId, state.currentDate.format("YYYY-MM-DD"));
    setChartData(response?.data);
  };

  useEffect(() => {
    getMainModel();
    getBackgroundModelUrl();
    getTaskData();
    getCostData();
  }, []);

  useEffect(() => {
    if (!userRole) return;
    getReportTableData();
  }, [userRole]);

  const convertBimToImage = (view) => {
    if (!view) {
      showError("Can't find view.");
      return null;
    }
    const world = view.world;
    const renderer = world._renderer.three;
    const scene = world._scene.three;
    const camera = world._camera.three;
    renderer.render(scene, camera);
    const imageDataUrl = renderer.domElement.toDataURL("image/png");
    return imageDataUrl;
  };

  const convertDomToImage = async (view) => {
    if (!view) {
      showError("Can't find view.");
      return null;
    }
    try {
      const item = await html2canvas(view);
      const imageDataUrl = item.toDataURL("image/png");
      return imageDataUrl;
    } catch {
      showError("Can't convert dom to image.");
      return null;
    }
  };

  // data url blob
  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(",")[1]);
    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }
    return new Blob([uint8Array], { type: mimeString });
  };

  const uploadSingleImageAndGetId = async (projectId, reportId, imageDataUrl, fileName) => {
    const formData = new FormData();
    formData.append("file", dataURItoBlob(imageDataUrl), fileName);

    try {
      const result = await uploadLocalImageApi(projectId, reportId, formData);
      if (result && result.data && result.data.reportImageId) {
        return result.data.reportImageId;
      } else {
        showError("Upload failed.");
        return null;
      }
    } catch {
      showError("Upload failed.");
      return null;
    }
  };

  const uploadSinglePDFAndGetId = async (projectId, reportId, file) => {
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const result = await uploadAppendixPDFApi(projectId, reportId, formData);
        if (result && result.data && result.data.appendixFileId) {
          return result.data.appendixFileId;
        } else {
          showError("Upload failed.");
          return null;
        }
      } catch {
        showError("Upload failed.");
        return null;
      }
    }
  };

  //generate daily report template
  const generateDailyReportTemplate = (dailyReportState, signedby) => [
    {
      fieldId: "[YYYY/MM/DD]",
      fieldValue: dailyReportState.clickedDailyDate,
    },
    {
      fieldId: "[Contractor Name]",
      fieldValue: dailyReportState.prepareByCompany,
    },
    {
      fieldId: "[Project_Title]",
      fieldValue: projectName,
    },
    {
      fieldId: "[Date]",
      fieldValue: dailyReportState.clickedDailyDateFormat,
    },
    {
      fieldId: "[Temperature_Value_1 (Numeric)]",
      fieldValue: dailyReportState.temperatureStart,
    },
    {
      fieldId: "[Temperature_Value_2 (Numeric)]",
      fieldValue: dailyReportState.temperatureEnd,
    },
    {
      fieldId: "[Weather (Free text)]",
      fieldValue:
        dailyReportState.weatherValue && dailyReportState.weatherValue.length > 0
          ? dailyReportState.weatherValue.filter((item) => item.trim() !== "").join("; ")
          : "",
    },
    {
      fieldId: "[Weather Warning (Value)]",
      fieldValue: dailyReportState.weatherWarningValue.map((item) => item.category).join("; "),
    },
    {
      fieldId: "[Weather Warning (Time)]",
      fieldValue: dailyReportState.weatherWarningValue.map((item) => item.value).join("; "),
    },
    {
      fieldId: "[Manhour_Category (Free text)]",
      fieldValue: dailyReportState.ManhourArray.map((item) => item.category).join("; "),
    },
    {
      fieldId: "[Manhour_Value (Numeric)]",
      fieldValue: dailyReportState.ManhourArray.map((item) => item.value).join("; "),
    },
    {
      fieldId: "[Equipment_Category (Free text)]",
      fieldValue: dailyReportState.EquipmentArray.map((item) => item.category).join("; "),
    },
    {
      fieldId: "[Equipment_Value (Numeric)]",
      fieldValue: dailyReportState.EquipmentArray.map((item) => item.value).join("; "),
    },
    {
      fieldId: "[Fatal_Value]",
      fieldValue: dailyReportState.safetyTableData[0].Count,
    },
    {
      fieldId: "[Days_Away_from_Works_Value]",
      fieldValue: dailyReportState.safetyTableData[1].Count,
    },
    {
      fieldId: "[Restricted_Work_Value]",
      fieldValue: dailyReportState.safetyTableData[2].Count,
    },
    {
      fieldId: "[Medical_Treatment_Value]",
      fieldValue: dailyReportState.safetyTableData[3].Count,
    },
    {
      fieldId: "[First_Aid_Treatment_Value]",
      fieldValue: dailyReportState.safetyTableData[4].Count,
    },
    {
      fieldId: "[Near_Miss_Value]",
      fieldValue: dailyReportState.safetyTableData[5].Count,
    },
    {
      fieldId: "[Free text Other HSSE Remarks]",
      fieldValue: dailyReportState.mainFreeEditorItems[0].content,
    },
    {
      fieldId: "[Free text Works Completed Today]",
      fieldValue: dailyReportState.mainFreeEditorItems[1].content,
    },
    {
      fieldId: "[Free text Works Planned for Next Day]",
      fieldValue: dailyReportState.mainFreeEditorItems[2].content,
    },
    {
      fieldId: "[Free text Any Other Matter or Concerns]",
      fieldValue: dailyReportState.mainFreeEditorItems[3].content,
    },
    {
      fieldId: "(New Free Section Name)",
      fieldValue: dailyReportState.newFreeEditorItems.map((item) => item.title).join("; ") || "",
    },
    {
      fieldId: "(New Free Section Content)",
      fieldValue: dailyReportState.newFreeEditorItems.map((item) => item.content).join("; ") || "",
    },
    {
      fieldId: "{Upload from Project site photo library | Upload local file}",
      fieldValue: dailyReportState.uploadedImages.map((image) => image.templateId).join("; "),
    },
    {
      fieldId: "[Signed by]",
      fieldValue: signedby || "",
    },
  ];

  //generate weekly report template
  const generateWeeklyReportTemplate = (weeklyReportState, currentModelImgId, planModelImgId, ganttChartImgId, appendixPDFId, signedby) => [
    {
      fieldId: "[From (YYYY/MM/DD)]",
      fieldValue: weeklyReportState.clickedWeeklyStartDate || "",
    },
    {
      fieldId: "[To (YYYY/MM/DD)]",
      fieldValue: weeklyReportState.clickedWeeklyEndDate || "",
    },
    {
      fieldId: "[Contractor Name]",
      fieldValue: weeklyReportState.prepareByCompany,
    },
    {
      fieldId: "[Project_Title]",
      fieldValue: projectName,
    },
    {
      fieldId: "[Date]",
      fieldValue: weeklyReportState.clickedWeeklyDateFormat || "",
    },
    {
      fieldId: "[Manhour Category (Summary)]",
      fieldValue: weeklyReportState.ManhourSummary.map((item) => item.category).join("; "),
    },
    {
      fieldId: "[Manhour Value (Summary)]",
      fieldValue: weeklyReportState.ManhourSummary.map((item) => item.value).join("; "),
    },
    {
      fieldId: "[Equipment Category (Summary)]",
      fieldValue: weeklyReportState.EquipmentSummary.map((item) => item.category).join("; "),
    },
    {
      fieldId: "[Equipment Value (Summary)]",
      fieldValue: weeklyReportState.EquipmentSummary.map((item) => item.value).join("; "),
    },
    {
      fieldId: "[Fatal_Value_Week]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Fatal");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Fatal_Value_All]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Fatal");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[Days_Away_from_Works_Value_Week]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Days Away from Works");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Days_Away_from_Works_Value_All]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Days Away from Works");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[Restricted_Work_Value_Week]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Restricted Work");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Restricted_Work_Value_All]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Restricted Work");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[Medical_Treatment_Value_Week]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Medical Treatment");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Medical_Treatment_Value_All]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Medical Treatment");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[First_Aid_Treatment_Value_Week]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "First Aid Treatment");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[First_Aid_Treatment_Value_All]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "First Aid Treatment");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[Near_Miss_Value_Week]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Near Miss");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Near_Miss_Value_All]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Near Miss");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[Observation_Value_Week]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Safety Reporting & Observation");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Observation_Value_All]",
      fieldValue: (() => {
        const data = weeklyReportState.GBGTableData.find((item) => item.label === "Safety Reporting & Observation");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[TRIR_Value]",
      fieldValue: weeklyReportState.TRIRValue,
    },
    {
      fieldId: "[LTIR_Value]",
      fieldValue: weeklyReportState.LTIRValue,
    },
    {
      fieldId: "[SIF_Actual_Value_All]",
      fieldValue: weeklyReportState.ActualValue,
    },
    {
      fieldId: "[SIF_Potential_Value_All]",
      fieldValue: weeklyReportState.PotentialAllValue,
    },
    {
      fieldId: "[SIF_Potential_Value_Current]",
      fieldValue: weeklyReportState.PotentialCurrentValue,
    },
    {
      fieldId: "[Free text Other HSSE Remarks]",
      fieldValue: weeklyReportState.otherHSSEFreeEditor,
    },
    {
      fieldId: "[Free text Procurement and Fabrication]",
      fieldValue: weeklyReportState.mainFreeEditorItems[0].content,
    },
    {
      fieldId: "[Free text Design and Engineering]",
      fieldValue: weeklyReportState.mainFreeEditorItems[1].content,
    },
    {
      fieldId: "[Free text Authorizations, Regulatory and Permitting]",
      fieldValue: weeklyReportState.mainFreeEditorItems[2].content,
    },
    {
      fieldId: "[Free text Site Construction Works]",
      fieldValue: weeklyReportState.mainFreeEditorItems[3].content,
    },
    {
      fieldId: "[Free text Testing and Commissioning]",
      fieldValue: weeklyReportState.mainFreeEditorItems[4].content,
    },
    {
      fieldId: "[Free text Quality Assurance and Quality Control]",
      fieldValue: weeklyReportState.mainFreeEditorItems[5].content,
    },
    {
      fieldId: "[Free text Schedule and Progress]",
      fieldValue: weeklyReportState.mainFreeEditorItems[6].content,
    },
    {
      fieldId: "{img1}",
      fieldValue: planModelImgId || "",
    },
    {
      fieldId: "{img2}",
      fieldValue: currentModelImgId || "",
    },
    {
      fieldId: "[Free text Works Planned for Next Week]",
      fieldValue: weeklyReportState.worksPlanFreeEditor,
    },
    {
      fieldId: "[Free text Any Other Matter or Concerns]",
      fieldValue: weeklyReportState.anyOtherMatterFreeEditor,
    },
    {
      fieldId: "(New Free Section Name)",
      fieldValue: weeklyReportState.newFreeEditorItems.map((item) => item.title).join("; "),
    },
    {
      fieldId: "(New Free Section Content)",
      fieldValue: weeklyReportState.newFreeEditorItems.map((item) => item.content).join("; "),
    },
    {
      fieldId: "{Upload from Project site photo library | Upload local file}",
      fieldValue: weeklyReportState.uploadedImages.map((image) => image.templateId).join("; "),
    },
    {
      fieldId: "{project gantt}",
      fieldValue: ganttChartImgId || "",
    },
    {
      fieldId: "[upload file from local device]",
      fieldValue: appendixPDFId || "",
    },
    {
      fieldId: "(New Free Appendix Name)",
      fieldValue: weeklyReportState.newAppendixItems.map((item) => item.title).join("; "),
    },
    {
      fieldId: "(New Free Appendix Content)",
      fieldValue: weeklyReportState.newAppendixItems.map((item) => item.content).join("; "),
    },
    {
      fieldId: "[Signed by]",
      fieldValue: signedby || "",
    },
  ];

  //generate monthly report template
  const generateMonthlyReportTemplate = (
    monthlyReportState,
    currentModelImgId,
    planModelImgId,
    ganttChartImgId,
    costChartImgId,
    appendixPDFId,
    signedby
  ) => [
    {
      fieldId: "[YYYY/MM]",
      fieldValue: monthlyReportState.clickedMonthlyDate || "",
    },
    {
      fieldId: "[Contractor Name]",
      fieldValue: monthlyReportState.prepareByCompany,
    },
    {
      fieldId: "[Project_Title]",
      fieldValue: projectName,
    },
    {
      fieldId: "[Date]",
      fieldValue: monthlyReportState.clickedMonthlyDateFormat || "",
    },
    {
      fieldId: "[Manhour Category (Summary)]",
      fieldValue: monthlyReportState.ManhourSummary.map((item) => item.category).join("; "),
    },
    {
      fieldId: "[Manhour Value (Summary)]",
      fieldValue: monthlyReportState.ManhourSummary.map((item) => item.value).join("; "),
    },
    {
      fieldId: "[Equipment Category (Summary)]",
      fieldValue: monthlyReportState.EquipmentSummary.map((item) => item.category).join("; "),
    },
    {
      fieldId: "[Equipment Value (Summary)]",
      fieldValue: monthlyReportState.EquipmentSummary.map((item) => item.value).join("; "),
    },
    {
      fieldId: "[Fatal_Value_Month]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Fatal");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Fatal_Value_All]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Fatal");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[Days_Away_from_Works_Value_Month]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Days Away from Works");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Days_Away_from_Works_Value_All]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Days Away from Works");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[Restricted_Work_Value_Month]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Restricted Work");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Restricted_Work_Value_All]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Restricted Work");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[Medical_Treatment_Value_Month]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Medical Treatment");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Medical_Treatment_Value_All]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Medical Treatment");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[First_Aid_Treatment_Value_Month]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "First Aid Treatment");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[First_Aid_Treatment_Value_All]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "First Aid Treatment");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[Near_Miss_Value_Month]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Near Miss");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Near_Miss_Value_All]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Near Miss");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[Observation_Value_Month]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Safety Reporting & Observation");
        return data ? data.headerDate0 : "0";
      })(),
    },
    {
      fieldId: "[Observation_Value_All]",
      fieldValue: (() => {
        const data = monthlyReportState.GBGTableData.find((item) => item.label === "Safety Reporting & Observation");
        return data ? data.headerDate1 : "0";
      })(),
    },
    {
      fieldId: "[TRIR_Value]",
      fieldValue: monthlyReportState.TRIRValue,
    },
    {
      fieldId: "[LTIR_Value]",
      fieldValue: monthlyReportState.LTIRValue,
    },
    {
      fieldId: "[SIF_Actual_Value_All]",
      fieldValue: monthlyReportState.ActualValue,
    },
    {
      fieldId: "[SIF_Potential_Value_All]",
      fieldValue: monthlyReportState.PotentialAllValue,
    },
    {
      fieldId: "[SIF_Potential_Value_Current]",
      fieldValue: monthlyReportState.PotentialCurrentValue,
    },
    {
      fieldId: "[Free text Other HSSE Remarks]",
      fieldValue: monthlyReportState.otherHSSEFreeEditor,
    },
    {
      fieldId: "[Free text Procurement and Fabrication]",
      fieldValue: monthlyReportState.mainFreeEditorItems[0].content,
    },
    {
      fieldId: "[Free text Design and Engineering]",
      fieldValue: monthlyReportState.mainFreeEditorItems[1].content,
    },
    {
      fieldId: "[Free text Authorizations, Regulatory and Permitting]",
      fieldValue: monthlyReportState.mainFreeEditorItems[2].content,
    },
    {
      fieldId: "[Free text Site Construction Works]",
      fieldValue: monthlyReportState.mainFreeEditorItems[3].content,
    },
    {
      fieldId: "[Free text Testing and Commissioning]",
      fieldValue: monthlyReportState.mainFreeEditorItems[4].content,
    },
    {
      fieldId: "[Free text Quality Assurance and Quality Control]",
      fieldValue: monthlyReportState.mainFreeEditorItems[5].content,
    },
    {
      fieldId: "[Free text Schedule and Progress]",
      fieldValue: monthlyReportState.mainFreeEditorItems[6].content,
    },
    {
      fieldId: "{img1}",
      fieldValue: planModelImgId || "",
    },
    {
      fieldId: "{img2}",
      fieldValue: currentModelImgId || "",
    },
    {
      fieldId: "[Free text Cost and Financials]",
      fieldValue: monthlyReportState.costFinancialsFreeEditor,
    },
    {
      fieldId: "{img4}",
      fieldValue: costChartImgId || "",
    },
    {
      fieldId: "[Free text Contract and Change Management]",
      fieldValue: monthlyReportState.contractManageFreeEditor,
    },
    {
      fieldId: "[Free text Works Planned for Next Week]",
      fieldValue: monthlyReportState.worksPlanFreeEditor,
    },
    {
      fieldId: "[Free text Any Other Matter or Concerns]",
      fieldValue: monthlyReportState.anyOtherMatterFreeEditor,
    },
    {
      fieldId: "(New Free Section Name)",
      fieldValue: monthlyReportState.newFreeEditorItems.map((item) => item.title).join("; "),
    },
    {
      fieldId: "(New Free Section Content)",
      fieldValue: monthlyReportState.newFreeEditorItems.map((item) => item.content).join("; "),
    },
    {
      fieldId: "{Upload from Project site photo library | Upload local file}",
      fieldValue: monthlyReportState.uploadedImages.map((image) => image.templateId).join("; "),
    },
    {
      fieldId: "{project gantt}",
      fieldValue: ganttChartImgId || "",
    },
    {
      fieldId: "[upload file from local device]",
      fieldValue: appendixPDFId || "",
    },
    {
      fieldId: "(New Free Appendix Name)",
      fieldValue: monthlyReportState.newAppendixItems.map((item) => item.title).join("; "),
    },
    {
      fieldId: "(New Free Appendix Content)",
      fieldValue: monthlyReportState.newAppendixItems.map((item) => item.content).join("; "),
    },
    {
      fieldId: "[Signed by]",
      fieldValue: signedby || "",
    },
  ];

  //save edit confirm  or complete report confirm
  const confirmSaveEditOrComplete = async (actionType) => {
    // call /save-report action-no need
    // call /save-report action-complete
    setState((prevState) => ({
      ...prevState,
      isLoadingFullScreen: true,
      layoutStyle: true,
      ganttChartHeight: false,
    }));
    await new Promise((resolve) => setTimeout(resolve, 0));
    let template;
    if (state.activeTab === "Daily Report") {
      template = generateDailyReportTemplate(dailyReportState);
    } else if (state.activeTab === "Weekly Report") {
      const currentModelImgBase = convertBimToImage(topBottomViewer.current.right);
      const planModelImgBase = convertBimToImage(topBottomViewer.current.left);
      const ganttChartImgBase = await convertDomToImage(ganttChartRef.current);
      try {
        const currentModelImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, currentModelImgBase, "currentModelImg.png");
        const planModelImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, planModelImgBase, "planModelImg.png");
        const ganttChartImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, ganttChartImgBase, "ganttChartImg.png");
        const appendixPDFId = await uploadSinglePDFAndGetId(projectId, state.selectedReportId, weeklyReportState.uploadedPDF);

        if (currentModelImgId && planModelImgId && ganttChartImgId) {
          template = generateWeeklyReportTemplate(
            weeklyReportState,
            `r${currentModelImgId}`,
            `r${planModelImgId}`,
            `r${ganttChartImgId}`,
            `${appendixPDFId}`
          );
        }
      } catch {
        showError("Operation failed.");
        setState((prevState) => ({
          ...prevState,
          isConfirming: false,
          isLoadingFullScreen: false,
          layoutStyle: false,
          ganttChartHeight: true,
        }));
      }
    } else if (state.activeTab === "Monthly Report") {
      const currentModelImgBase = convertBimToImage(topBottomViewer.current.right);
      const planModelImgBase = convertBimToImage(topBottomViewer.current.left);
      const ganttChartImgBase = await convertDomToImage(ganttChartRef.current);
      const costChartImgBase = await convertDomToImage(LineChartRef.current);

      try {
        const currentModelImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, currentModelImgBase, "currentModelImg.png");
        const planModelImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, planModelImgBase, "planModelImg.png");
        const ganttChartImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, ganttChartImgBase, "ganttChartImg.png");
        const costChartImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, costChartImgBase, "costChartImg.png");
        const appendixPDFId = await uploadSinglePDFAndGetId(projectId, state.selectedReportId, monthlyReportState.uploadedPDF);

        if (currentModelImgId && planModelImgId && ganttChartImgId && costChartImgId) {
          template = generateMonthlyReportTemplate(
            monthlyReportState,
            `r${currentModelImgId}`,
            `r${planModelImgId}`,
            `r${ganttChartImgId}`,
            `r${costChartImgId}`,
            `${appendixPDFId}`
          );
        }
      } catch {
        showError("Operation failed.");
        setState((prevState) => ({
          ...prevState,
          isConfirming: false,
          isLoadingFullScreen: false,
          layoutStyle: false,
          ganttChartHeight: true,
        }));
      }
    }
    try {
      const response = await saveEditApi(projectId, state.selectedRowData?.type, state.selectedReportId, userId, actionType, template);
      if (response) {
        showSuccess("The operation was successful!");
        setState((prevState) => ({
          ...prevState,
          isConfirming: false,
          isLoadingFullScreen: false,
          layoutStyle: false,
          ganttChartHeight: true,
        }));
        await getReportTableData();
      }
    } catch {
      showError("Operation failed.");
      setState((prevState) => ({
        ...prevState,
        isConfirming: false,
        isLoadingFullScreen: false,
        layoutStyle: false,
        ganttChartHeight: true,
      }));
    }
  };

  //retract report confirm
  const confirmRetract = async (retractRole) => {
    //retractRole -PM;-user
    //call /update-status action-retract  i.e. user no need input retract reason, pm need input retract reason
    try {
      const response = await updateReportStatusApi(
        projectId,
        state.selectedRowData?.type,
        "retract",
        state.selectedReportId,
        retractRole === "PM" ? state.retractReason : null
      );
      if (response) {
        showSuccess("Report has been successfully retracted.");
        setState((prevState) => ({
          ...prevState,
          retractPopup: false,
          retractReason: "",
        }));
        await getReportTableData();
      }
    } catch {
      showError("Retract failed.");
    }
  };

  //reject report confirm
  const confirmReject = async () => {
    // call /update-status action-reject
    try {
      const response = await updateReportStatusApi(projectId, state.selectedRowData?.type, "reject", state.selectedReportId, state.rejectionReason);
      if (response) {
        showSuccess("Report has been successfully rejected.");
        setState((prevState) => ({
          ...prevState,
          rejectPopup: false,
          rejectionReason: "",
        }));
        await getReportTableData();
      }
    } catch {
      showError("Reject failed.");
    }
  };

  // submit report confirm
  const confirmSubmit = async () => {
    //call /generate-report
    setState((prevState) => ({
      ...prevState,
      isLoadingFullScreen: true,
      layoutStyle: true,
      ganttChartHeight: false,
    }));
    await new Promise((resolve) => setTimeout(resolve, 0));
    let template;
    if (state.activeTab === "Daily Report") {
      template = generateDailyReportTemplate(dailyReportState, userWithCompany);
    } else if (state.activeTab === "Weekly Report") {
      const currentModelImgBase = convertBimToImage(topBottomViewer.current.right);
      const planModelImgBase = convertBimToImage(topBottomViewer.current.left);
      const ganttChartImgBase = await convertDomToImage(ganttChartRef.current);
      try {
        const currentModelImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, currentModelImgBase, "currentModelImg.png");
        const planModelImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, planModelImgBase, "planModelImg.png");
        const ganttChartImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, ganttChartImgBase, "ganttChartImg.png");
        const appendixPDFId = await uploadSinglePDFAndGetId(projectId, state.selectedReportId, weeklyReportState.uploadedPDF);

        if (currentModelImgId && planModelImgId && ganttChartImgId) {
          template = generateWeeklyReportTemplate(
            weeklyReportState,
            `r${currentModelImgId}`,
            `r${planModelImgId}`,
            `r${ganttChartImgId}`,
            `${appendixPDFId}`,
            userWithCompany
          );
        }
      } catch {
        showError("Submit failed.");
      }
    } else if (state.activeTab === "Monthly Report") {
      const currentModelImgBase = convertBimToImage(topBottomViewer.current.right);
      const planModelImgBase = convertBimToImage(topBottomViewer.current.left);
      const ganttChartImgBase = await convertDomToImage(ganttChartRef.current);
      const costChartImgBase = await convertDomToImage(LineChartRef.current);
      try {
        const currentModelImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, currentModelImgBase, "currentModelImg.png");
        const planModelImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, planModelImgBase, "planModelImg.png");
        const ganttChartImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, ganttChartImgBase, "ganttChartImg.png");
        const costChartImgId = await uploadSingleImageAndGetId(projectId, state.selectedReportId, costChartImgBase, "costChartImg.png");
        const appendixPDFId = await uploadSinglePDFAndGetId(projectId, state.selectedReportId, monthlyReportState.uploadedPDF);

        if (currentModelImgId && planModelImgId && ganttChartImgId && costChartImgId) {
          template = generateMonthlyReportTemplate(
            monthlyReportState,
            `r${currentModelImgId}`,
            `r${planModelImgId}`,
            `r${ganttChartImgId}`,
            `r${costChartImgId}`,
            `${appendixPDFId}`,
            userWithCompany
          );
        }
      } catch {
        showError("Submit failed.");
      }
    }
    try {
      const response = await submitReportApi(projectId, state.selectedRowData?.type, state.selectedReportId, template);
      if (response) {
        showSuccess("Report has been successfully submitted.");
        setState((prevState) => ({
          ...prevState,
          isConfirming: false,
          isLoadingFullScreen: false,
          layoutStyle: false,
          ganttChartHeight: true,
        }));
        await getReportTableData();
      }
    } catch {
      showError("Submit failed.");
    }
  };

  //weather warning time
  const parseTimeRange = (value) => {
    if (!value || !value.includes("-")) {
      return { startTime: "", endTime: "" };
    }
    const [startTime, endTime] = value.split("-");
    return { startTime, endTime };
  };

  //weather warning time
  const formatTimeRange = (startTime, endTime) => {
    if (!startTime && !endTime) return "";
    return `${startTime || "00:00"}-${endTime || "23:55"}`;
  };

  const renderDailyReport = useCallback(() => {
    return (
      <Box width="100%" height="calc(100% - 64px)">
        {state.currentLockStatus ? (
          <Box height={28}></Box>
        ) : (
          <RoleBaseActionButtons
            reportStatus={state.selectedRowData?.statusNumber}
            setState={setState}
            reportReadOnly={state.selectedRowData?.shouldBeReadonly}
          />
        )}
        <Box sx={{ height: "100%", overflow: "auto", px: 2, pb: 2, mt: 1 }}>
          <ReportTextTitle content={"General"} />
          <Grid container>
            <Grid item xs={3}>
              Report Name
            </Grid>
            <Grid item xs={9}>
              {state.reportName}
            </Grid>
            <Grid item xs={3} pt={0.3}>
              Title of Project
            </Grid>
            <Grid item xs={9} pt={0.3}>
              {projectName}
            </Grid>
            <Grid item xs={3} pt={0.3}>
              Date
            </Grid>
            <Grid item xs={9} pt={0.3}>
              {dailyReportState.clickedDailyDateFormat}
            </Grid>
            <Grid item xs={3} pt={0.3} alignSelf="center">
              Temperature
            </Grid>
            <Grid item xs={9} pt={0.3}>
              <TextField
                variant="outlined"
                type="number"
                disabled={state.readonly}
                sx={{
                  width: "50px",
                  "& .MuiInputBase-input": { px: 1, py: 0.3 },
                }}
                value={dailyReportState.temperatureStart}
                onChange={(e) => {
                  setDailyReportState((prevState) => ({
                    ...prevState,
                    temperatureStart: e.target.value,
                  }));
                }}
              />
              <span style={{ verticalAlign: "bottom" }}> ℃ to </span>
              <TextField
                variant="outlined"
                type="number"
                disabled={state.readonly}
                sx={{
                  width: "50px",
                  "& .MuiInputBase-input": { px: 1, py: 0.3 },
                }}
                value={dailyReportState.temperatureEnd}
                onChange={(e) => {
                  setDailyReportState((prevState) => ({
                    ...prevState,
                    temperatureEnd: e.target.value,
                  }));
                }}
              />
              <span style={{ verticalAlign: "bottom" }}> ℃</span>
            </Grid>
            <Grid item xs={3} pt={0.6} alignSelf="center">
              Weather
            </Grid>
            <Grid item xs={9} pt={0.6} alignSelf="center">
              <SelectComponent
                disabled={state.readonly}
                isMultiSelect
                options={[
                  { label: "Sunny", value: "Sunny" },
                  { label: "Cloudy", value: "Cloudy" },
                  { label: "Rain", value: "Rain" },
                  { label: "Showers", value: "Showers" },
                  { label: "Thunderstorm", value: "Thunderstorm" },
                ]}
                value={dailyReportState.weatherValue}
                placeholder="Weather"
                onChange={(value) => {
                  setDailyReportState((prevState) => ({
                    ...prevState,
                    weatherValue: value,
                  }));
                }}
                selectStyle={{
                  "& .MuiSelect-select": {
                    py: 0.5,
                    pl: 1,
                  },
                }}
              />
            </Grid>
            <Grid item xs={3} pt={"11px"}>
              Weather Warning
            </Grid>
            <Grid item xs={9} pt={0.3} alignSelf="center">
              {dailyReportState.weatherWarningValue?.map((row, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <SelectComponent
                    disabled={state.readonly}
                    options={dailyReportState.weatherWarningOptions}
                    value={row.category}
                    placeholder="Weather Warning"
                    onChange={(value) => {
                      setDailyReportState((prevState) => {
                        const updatedRows = [...prevState.weatherWarningValue];
                        updatedRows[index].category = value;
                        return {
                          ...prevState,
                          weatherWarningValue: updatedRows,
                        };
                      });
                    }}
                    selectStyle={{
                      "& .MuiSelect-select": {
                        py: 0.5,
                        pl: 1,
                      },
                    }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      label="Start"
                      value={parseTimeRange(row.value).startTime ? dayjs(parseTimeRange(row.value).startTime, "HH:mm") : null}
                      onChange={(newStartTime) => {
                        const startStr = newStartTime ? newStartTime.format("HH:mm") : "";
                        let { endTime } = parseTimeRange(row.value || "00:00-23:55");
                        if (startStr && endTime && startStr > endTime) {
                          endTime = startStr;
                          showError("The start time cannot be greater than the end time.");
                        }
                        const newTimeRange = formatTimeRange(startStr, endTime);
                        setDailyReportState((prevState) => {
                          const updatedRows = [...prevState.weatherWarningValue];
                          updatedRows[index].value = newTimeRange;
                          return {
                            ...prevState,
                            weatherWarningValue: updatedRows,
                          };
                        });
                      }}
                      ampm={false}
                      format="HH:mm"
                      disabled={state.readonly}
                      slotProps={{
                        textField: {
                          InputLabelProps: { shrink: true },
                          sx: {
                            width: 120,
                            "& .MuiInputBase-input": { px: 1, py: 0.5 },
                          },
                        },
                        actionBar: {
                          actions: [],
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      label="End"
                      value={parseTimeRange(row.value).endTime ? dayjs(parseTimeRange(row.value).endTime, "HH:mm") : null}
                      onChange={(newEndTime) => {
                        const endStr = newEndTime ? newEndTime.format("HH:mm") : "";
                        let { startTime } = parseTimeRange(row.value || "00:00-23:55");
                        if (endStr && startTime && endStr < startTime) {
                          startTime = endStr;
                          showError("The end time cannot be less than the start time.");
                        }
                        const newTimeRange = formatTimeRange(startTime, endStr);
                        setDailyReportState((prevState) => {
                          const updatedRows = [...prevState.weatherWarningValue];
                          updatedRows[index].value = newTimeRange;
                          return {
                            ...prevState,
                            weatherWarningValue: updatedRows,
                          };
                        });
                      }}
                      ampm={false}
                      format="HH:mm"
                      disabled={state.readonly}
                      slotProps={{
                        textField: {
                          InputLabelProps: { shrink: true },
                          sx: {
                            width: 120,
                            "& .MuiInputBase-input": { px: 1, py: 0.5 },
                          },
                        },
                        actionBar: {
                          actions: [],
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {/* <TextField
                    disabled={state.readonly}
                    id="startTime"
                    label="Start"
                    type="time"
                    value={parseTimeRange(row.value).startTime}
                    onChange={(e) => {
                      const newStartTime = e.target.value;
                      let newEndTime = parseTimeRange(
                        row.value || "00:00-23:59"
                      ).endTime;
                      if (
                        newStartTime &&
                        newEndTime &&
                        newStartTime > newEndTime
                      ) {
                        newEndTime = newStartTime;
                        showError(
                          "The start time cannot be greater than the end time."
                        );
                      }
                      const newTimeRange = formatTimeRange(
                        newStartTime,
                        newEndTime
                      );
                      setDailyReportState((prevState) => {
                        const updatedRows = [...prevState.weatherWarningValue];

                        updatedRows[index].value = newTimeRange;
                        return {
                          ...prevState,
                          weatherWarningValue: updatedRows,
                        };
                      });
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    sx={{
                      width: 120,
                      "& .MuiInputBase-input": { px: 1, py: 0.5 },
                    }}
                  />
                  <TextField
                    disabled={state.readonly}
                    id="endTime"
                    label="End"
                    type="time"
                    value={parseTimeRange(row.value).endTime}
                    onChange={(e) => {
                      const newEndTime = e.target.value;
                      let newStartTime = parseTimeRange(
                        row.value || "00:00-23:59"
                      ).startTime;
                      if (
                        newEndTime &&
                        newStartTime &&
                        newEndTime < newStartTime
                      ) {
                        newStartTime = newEndTime;
                        showError(
                          "The end time cannot be less than the start time."
                        );
                      }
                      const newTimeRange = formatTimeRange(
                        newStartTime,
                        newEndTime
                      );
                      setDailyReportState((prevState) => {
                        const updatedRows = [...prevState.weatherWarningValue];
                        updatedRows[index].value = newTimeRange;
                        return {
                          ...prevState,
                          weatherWarningValue: updatedRows,
                        };
                      });
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    sx={{
                      width: 120,
                      "& .MuiInputBase-input": { px: 1, py: 0.5 },
                    }}
                  /> */}
                  {/* <TextField
                    disabled={state.readonly}
                    placeholder="Time"
                    value={row.value}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setDailyReportState((prevState) => {
                        const updatedRows = [...prevState.weatherWarningValue];
                        updatedRows[index].value = newValue;
                        return {
                          ...prevState,
                          weatherWarningValue: updatedRows,
                        };
                      });
                    }}
                    sx={{
                      width: 140,
                      "& .MuiInputBase-input": {
                        px: 1,
                        py: 0.5,
                      },
                    }}
                  /> */}
                  {!state.readonly && (
                    <RemoveCircleOutline
                      onClick={() => {
                        if (dailyReportState.weatherWarningValue.length > 1) {
                          setDailyReportState((prevState) => {
                            const updatedRows = prevState.weatherWarningValue.filter((_, i) => i !== index);
                            return {
                              ...prevState,
                              weatherWarningValue: updatedRows,
                            };
                          });
                        }
                      }}
                      sx={{
                        cursor: "pointer",
                        color: dailyReportState.weatherWarningValue.length > 1 ? "#858585" : "#ccc",
                        pointerEvents: dailyReportState.weatherWarningValue.length > 1 ? "auto" : "none",
                      }}
                    />
                  )}
                  {index === dailyReportState.weatherWarningValue.length - 1 && !state.readonly && (
                    <AddCircleOutline
                      onClick={() => {
                        setDailyReportState((prevState) => {
                          const newRow = { category: "", value: "" };
                          return {
                            ...prevState,
                            weatherWarningValue: [...prevState.weatherWarningValue, newRow],
                          };
                        });
                      }}
                      sx={{ cursor: "pointer", color: "#858585" }}
                    />
                  )}
                </Box>
              ))}
            </Grid>
            <Grid item xs={3} pt={0.3}>
              Contractor Name
            </Grid>
            <Grid item xs={9} pt={0.3}>
              {dailyReportState.prepareByCompany}
            </Grid>
            <Grid item xs={3} pt={1}>
              Manhour
            </Grid>
            <Grid item xs={9} pt={0.3}>
              <AddTextField onChange={manhourChange} textValue={dailyReportState.ManhourArray} readonly={state.readonly} />
            </Grid>
            <Grid item xs={3} pt={1}>
              Equipment
            </Grid>
            <Grid item xs={9} pt={0.3}>
              <AddTextField onChange={equipmentChange} textValue={dailyReportState.EquipmentArray} readonly={state.readonly} />
            </Grid>
          </Grid>
          <ReportTextTitle content={"Safety"} />
          <SafetyPerformanceTable safetyTableData={dailyReportState.safetyTableData} onCountChange={changeSafetyTable} readonly={state.readonly} />
          {dailyReportState.mainFreeEditorItems.map((item) => (
            <RichTextEditor
              readonly={state.readonly}
              key={item.id}
              id={item.id}
              title={item.title}
              initialValue={item.content}
              onChange={(id, value) => {
                setDailyReportState((prevState) => ({
                  ...prevState,
                  mainFreeEditorItems: prevState.mainFreeEditorItems.map((i) => (i.id === id ? { ...i, content: value } : i)),
                }));
              }}
            />
          ))}
          {dailyReportState.newFreeEditorItems.map((item) => (
            <AddFreeEditorItem
              readonly={state.readonly}
              key={item.id}
              item={item}
              onTitleChange={(id, value) => {
                setDailyReportState((prevState) => ({
                  ...prevState,
                  newFreeEditorItems: prevState.newFreeEditorItems.map((i) => (i.id === id ? { ...item, title: value } : i)),
                }));
              }}
              onContentChange={(id, value) => {
                setDailyReportState((prevState) => ({
                  ...prevState,
                  newFreeEditorItems: prevState.newFreeEditorItems.map((i) => (i.id === id ? { ...i, content: value } : i)),
                }));
              }}
              onRemove={(id) => {
                setDailyReportState((prevState) => ({
                  ...prevState,
                  newFreeEditorItems: prevState.newFreeEditorItems.filter((item) => item.id !== id),
                }));
              }}
            />
          ))}
          {!state.readonly && <AddButtonWithLines onAdd={addNewDailyFreeEditor} />}
          <UploadPhoto
            readonly={state.readonly}
            title="Photos"
            images={dailyReportState.uploadedImages}
            onDelete={(index) => {
              const updatedImages = dailyReportState.uploadedImages.filter((_, i) => i !== index);
              setDailyReportState((prevState) => ({
                ...prevState,
                uploadedImages: updatedImages,
              }));
              URL.revokeObjectURL(dailyReportState.uploadedImages[index].url);
            }}
            onSitePhotoClick={() => {
              setState((prevState) => ({
                ...prevState,
                sitePhotoPopup: true,
              }));
            }}
            onLocalFileChange={(event) => {
              dailyImagesChange(event);
            }}
          />
          <ReportTextTitle content={"Signed By"} sx={{ display: "inline-block" }} />
          <ReportTextTitle
            content={dailyReportState.signedby}
            sx={{
              fontSize: "12px",
              fontWeight: "normal",
              ml: 2,
              display: "inline-block",
            }}
          />
        </Box>
      </Box>
    );
  }, [state.reportName, state.selectedReportId, state.selectedRowData, state.currentLockStatus, projectName, userWithCompany, dailyReportState]);

  const renderWeeklyReport = useCallback(() => {
    return (
      <Box width="100%" height="calc(100% - 64px)">
        {state.selectedRowData?.allDailyReportSubmitted === false && (
          <TooltipInfo
            sx={{
              borderColor: "#FF0000",
              left: "50px",
              top: "65px",
              maxWidth: "540px",
              px: 2,
              py: 1,
              color: "#FF0000",
            }}
            icon={
              <ErrorOutline
                sx={{
                  position: "absolute",
                  left: "50px",
                  top: "41px",
                  color: "#FF0000",
                }}
              />
            }
            content={"Please review and approve all daily report of the week before moving on to weekly report."}
          />
        )}
        {state.currentLockStatus ? (
          <Box height={28}></Box>
        ) : (
          <RoleBaseActionButtons
            reportStatus={state.selectedRowData?.statusNumber}
            allDailyReportSubmitted={state.selectedRowData?.allDailyReportSubmitted}
            setState={setState}
            reportReadOnly={state.selectedRowData?.shouldBeReadonly}
          />
        )}
        <Box sx={{ height: "100%", overflow: "auto", px: 2, pb: 2, mt: 1 }}>
          <ReportTextTitle content={"General"} />
          <ReportSummaryGrid
            reportName={state.reportName}
            projectName={projectName}
            company={weeklyReportState.prepareByCompany}
            manhourSummary={weeklyReportState.ManhourSummary}
            equipmentSummary={weeklyReportState.EquipmentSummary}
            clickedDate={weeklyReportState.clickedWeeklyDateFormat}
          />
          <ReportTextTitle content={"Safety"} />
          <Box height={350}>
            <SafetyChartTable
              title="Overall Safety Performance"
              tableData={weeklyReportState.GBGTableData}
              headerDateColumns={weeklyReportState.safetyHeadDate}
              TRIRValue={weeklyReportState.TRIRValue}
              LTIRValue={weeklyReportState.LTIRValue}
            />
          </Box>
          <RichTextEditor
            readonly={state.readonly}
            id={"Other HSSE Remarks"}
            title={"Other HSSE Remarks"}
            initialValue={weeklyReportState.otherHSSEFreeEditor}
            onChange={(id, value) => {
              setWeeklyReportState((prevState) => ({
                ...prevState,
                otherHSSEFreeEditor: value,
              }));
            }}
          />
          <ReportTextTitle content={"Works Completed This Week"} />
          {weeklyReportState.mainFreeEditorItems.map((item) => (
            <RichTextEditor
              readonly={state.readonly}
              key={item.id}
              id={item.id}
              title={item.title}
              initialValue={item.content}
              titleStyle={{ fontSize: "16px", fontWeight: 500 }}
              onChange={(id, value) => {
                setWeeklyReportState((prevState) => ({
                  ...prevState,
                  mainFreeEditorItems: prevState.mainFreeEditorItems.map((i) => (i.id === id ? { ...i, content: value } : i)),
                }));
              }}
            />
          ))}
          <ReportTextTitle content={"Actual Model vs Planned Model"} sx={{ fontSize: "16px", fontWeight: 500 }} />
          <ReportTextTitle
            content={"Please refer to Appendix 1 for further detail of schedule and progress"}
            sx={{ fontSize: "12px", fontWeight: "normal" }}
          />
          <Box height={800}>
            {!state.readonly ? (
              <TopBottomViewer
                ref={topBottomViewer}
                modelURL={state.modelURL}
                backgroundModels={state.backgroundModels}
                topTitle={"Actual Model View"}
                bottomTitle={"Plan Model View"}
                elementIds={elementIds}
              />
            ) : (
              <>
                <Container title="Actual Model View" icon="/assets/icons/report.svg" sx={{ height: "49%" }}>
                  {weeklyReportState.currentModelImg && (
                    <img
                      src={weeklyReportState.currentModelImg}
                      alt="Actual Model"
                      key="current-model-img"
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                      }}
                    />
                  )}
                </Container>
                <Container title="Plan Model View" icon="/assets/icons/report.svg" sx={{ height: "49%", mt: 1 }}>
                  {weeklyReportState.planModelImg && (
                    <img
                      src={weeklyReportState.planModelImg}
                      alt="Plan Model"
                      key="plan-model-img"
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                      }}
                    />
                  )}
                </Container>
              </>
            )}
          </Box>
          <RichTextEditor
            readonly={state.readonly}
            id={"Works Planned for Next Week"}
            title={"Works Planned for Next Week"}
            initialValue={weeklyReportState.worksPlanFreeEditor}
            onChange={(id, value) => {
              setWeeklyReportState((prevState) => ({
                ...prevState,
                worksPlanFreeEditor: value,
              }));
            }}
          />
          <RichTextEditor
            readonly={state.readonly}
            id={"Any Other Matter or Concerns"}
            title={"Any Other Matter or Concerns"}
            initialValue={weeklyReportState.anyOtherMatterFreeEditor}
            onChange={(id, value) => {
              setWeeklyReportState((prevState) => ({
                ...prevState,
                anyOtherMatterFreeEditor: value,
              }));
            }}
          />
          {weeklyReportState.newFreeEditorItems.map((item) => (
            <AddFreeEditorItem
              readonly={state.readonly}
              key={item.id}
              item={item}
              onTitleChange={(id, value) => {
                setWeeklyReportState((prevState) => ({
                  ...prevState,
                  newFreeEditorItems: prevState.newFreeEditorItems.map((i) => (i.id === id ? { ...item, title: value } : i)),
                }));
              }}
              onContentChange={(id, value) => {
                setWeeklyReportState((prevState) => ({
                  ...prevState,
                  newFreeEditorItems: prevState.newFreeEditorItems.map((i) => (i.id === id ? { ...i, content: value } : i)),
                }));
              }}
              onRemove={(id) => {
                setWeeklyReportState((prevState) => ({
                  ...prevState,
                  newFreeEditorItems: prevState.newFreeEditorItems.filter((item) => item.id !== id),
                }));
              }}
            />
          ))}
          {!state.readonly && <AddButtonWithLines onAdd={addNewWeeklyFreeEditor} />}
          <UploadPhoto
            readonly={state.readonly}
            title="Photos"
            images={weeklyReportState.uploadedImages}
            onDelete={(index) => {
              const updatedImages = weeklyReportState.uploadedImages.filter((_, i) => i !== index);
              setWeeklyReportState((prevState) => ({
                ...prevState,
                uploadedImages: updatedImages,
              }));
              URL.revokeObjectURL(weeklyReportState.uploadedImages[index].url);
            }}
            onSitePhotoClick={() => {
              setState((prevState) => ({
                ...prevState,
                sitePhotoPopup: true,
              }));
            }}
            onLocalFileChange={(event) => {
              weeklyImagesChange(event);
            }}
          />
          <ReportTextTitle content={"Signed By"} sx={{ display: "inline-block" }} />
          <ReportTextTitle
            content={weeklyReportState.signedby}
            sx={{
              fontSize: "12px",
              fontWeight: "normal",
              ml: 2,
              display: "inline-block",
            }}
          />
          <ReportTextTitle content={"Appendix 1"} />
          <ReportTextTitle content={"Schedule Gantt Chart"} sx={{ fontSize: "12px" }} />
          {!state.readonly ? (
            <Box ref={ganttChartRef} height={state.ganttChartHeight ? 390 : "auto"}>
              <ScheduleGantt
                tasks={state.taskTableData}
                startDate={state.startDate}
                endDate={state.endDate}
                currentDate={state.currentDate}
                SimplifiedVersion={true}
                defaultExpandAll={true}
                setCurrentDate={(d) =>
                  setState((prevState) => ({
                    ...prevState,
                    currentDate: d ? dayjs(d) : null,
                  }))
                }
                miniColumnWidth={22}
                treeTableHeaders={treeTableHeaders}
                selectedRowId={state.selectedGanttRowId}
                handleRowClick={(row) => {
                  setState((prevState) => ({
                    ...prevState,
                    selectedGanttRowId: row.taskId,
                  }));
                }}
              ></ScheduleGantt>
            </Box>
          ) : (
            <>
              {weeklyReportState.ganttChartImg && (
                <img style={{ width: "100%" }} src={weeklyReportState.ganttChartImg} alt="Gantt Chart" key="gantt-chart-img" />
              )}
            </>
          )}
          <ReportTextTitle content={"Appendix 2"} />
          <ReportTextTitle content={"3 Week Look-ahead Schedule"} sx={{ fontSize: "12px" }} />
          <UploadPDF
            readonly={state.readonly}
            onFileChange={(file) => {
              setWeeklyReportState((prevState) => ({
                ...prevState,
                uploadedPDF: file,
              }));
            }}
            uploadedPDF={weeklyReportState.uploadedPDF}
          />
          {weeklyReportState.newAppendixItems.map((item, index) => (
            <AddFreeEditorItem
              readonly={state.readonly}
              Appendix
              index={index}
              key={item.id}
              item={item}
              onTitleChange={(id, value) => {
                setWeeklyReportState((prevState) => ({
                  ...prevState,
                  newAppendixItems: prevState.newAppendixItems.map((i) => (i.id === id ? { ...item, title: value } : i)),
                }));
              }}
              onContentChange={(id, value) => {
                setWeeklyReportState((prevState) => ({
                  ...prevState,
                  newAppendixItems: prevState.newAppendixItems.map((i) => (i.id === id ? { ...i, content: value } : i)),
                }));
              }}
              onRemove={(id) => {
                setWeeklyReportState((prevState) => ({
                  ...prevState,
                  newAppendixItems: prevState.newAppendixItems.filter((item) => item.id !== id),
                }));
              }}
            />
          ))}
          {!state.readonly && <AddButtonWithLines onAdd={addNewWeeklyAppendix} />}
        </Box>
      </Box>
    );
  }, [
    state.reportName,
    state.selectedReportId,
    state.selectedRowData,
    state.ganttChartHeight,
    state.currentLockStatus,
    state.selectedGanttRowId,
    projectName,
    userWithCompany,
    weeklyReportState,
  ]);

  const renderMonthlyReport = useCallback(() => {
    return (
      <Box width="100%" height="calc(100% - 64px)">
        {state.currentLockStatus ? (
          <Box height={28}></Box>
        ) : (
          <RoleBaseActionButtons
            reportStatus={state.selectedRowData?.statusNumber}
            setState={setState}
            reportReadOnly={state.selectedRowData?.shouldBeReadonly}
          />
        )}
        <Box sx={{ height: "100%", overflow: "auto", px: 2, pb: 2, mt: 1 }}>
          <ReportTextTitle content={"General"} />
          <ReportSummaryGrid
            reportName={state.reportName}
            projectName={projectName}
            company={monthlyReportState.prepareByCompany}
            manhourSummary={monthlyReportState.ManhourSummary}
            equipmentSummary={monthlyReportState.EquipmentSummary}
            clickedDate={monthlyReportState.clickedMonthlyDateFormat}
          />
          <ReportTextTitle content={"Safety"} />
          <Box height={350}>
            <SafetyChartTable
              title="Overall Safety Performance"
              tableData={monthlyReportState.GBGTableData}
              headerDateColumns={monthlyReportState.safetyHeadDate}
              TRIRValue={monthlyReportState.TRIRValue}
              LTIRValue={monthlyReportState.LTIRValue}
            />
          </Box>
          <RichTextEditor
            readonly={state.readonly}
            id={"Other HSSE Remarks"}
            title={"Other HSSE Remarks"}
            initialValue={monthlyReportState.otherHSSEFreeEditor}
            onChange={(id, value) => {
              setMonthlyReportState((prevState) => ({
                ...prevState,
                otherHSSEFreeEditor: value,
              }));
            }}
          />
          <ReportTextTitle content={"Works Completed This Month"} />
          {monthlyReportState.mainFreeEditorItems.map((item) => (
            <RichTextEditor
              readonly={state.readonly}
              id={item.id}
              key={item.id}
              title={item.title}
              initialValue={item.content}
              titleStyle={{ fontSize: "16px", fontWeight: 500 }}
              onChange={(id, value) => {
                setMonthlyReportState((prevState) => ({
                  ...prevState,
                  mainFreeEditorItems: prevState.mainFreeEditorItems.map((i) => (i.id === id ? { ...i, content: value } : i)),
                }));
              }}
            />
          ))}
          <ReportTextTitle content={"Actual Model vs Planned Model"} sx={{ fontSize: "16px", fontWeight: 500 }} />
          <ReportTextTitle
            content={"Please refer to Appendix 1 for further detail of schedule and progress"}
            sx={{ fontSize: "12px", fontWeight: "normal" }}
          />
          <Box height={800}>
            {!state.readonly ? (
              <TopBottomViewer
                ref={topBottomViewer}
                modelURL={state.modelURL}
                backgroundModels={state.backgroundModels}
                topTitle={"Actual Model View"}
                bottomTitle={"Plan Model View"}
                elementIds={elementIds}
              />
            ) : (
              <>
                <Container title="Actual Model View" icon="/assets/icons/report.svg" sx={{ height: "49%" }}>
                  {monthlyReportState.currentModelImg && (
                    <img
                      src={monthlyReportState.currentModelImg}
                      alt="Actual Model"
                      key="current-model-img"
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                      }}
                    />
                  )}
                </Container>
                <Container title="Plan Model View" icon="/assets/icons/report.svg" sx={{ height: "49%", mt: 1 }}>
                  {monthlyReportState.planModelImg && (
                    <img
                      src={monthlyReportState.planModelImg}
                      alt="Plan Model"
                      key="plan-model-img"
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                      }}
                    />
                  )}
                </Container>
              </>
            )}
          </Box>
          <RichTextEditor
            readonly={state.readonly}
            id={"Cost and Financials"}
            title={"Cost and Financials"}
            initialValue={monthlyReportState.costFinancialsFreeEditor}
            titleStyle={{ fontSize: "16px", fontWeight: 500 }}
            onChange={(id, value) => {
              setMonthlyReportState((prevState) => ({
                ...prevState,
                costFinancialsFreeEditor: value,
              }));
            }}
          />
          <ReportTextTitle content={"Earned Value"} sx={{ fontSize: "16px", fontWeight: 500 }} />
          <Box ref={LineChartRef} sx={{ height: "200px" }}>
            {!state.readonly ? (
              <Box sx={{ height: "100%", width: "100%", border: "1px solid #ccc" }}>
                <LineBarChart
                  data={chartData}
                  options={{
                    "Earned Value": { color: "#318E6C", type: "line" },
                  }}
                  legendField={"name"}
                  xAxisField={"time"}
                  yAxisField={"value"}
                ></LineBarChart>
              </Box>
            ) : (
              <>{monthlyReportState.costChartImg && <img src={monthlyReportState.costChartImg} alt="Earned Value" key="earned-value-img" />}</>
            )}
          </Box>
          <RichTextEditor
            readonly={state.readonly}
            id={"Contract and Change Management"}
            title={"Contract and Change Management"}
            initialValue={monthlyReportState.contractManageFreeEditor}
            titleStyle={{ fontSize: "16px", fontWeight: 500 }}
            onChange={(id, value) => {
              setMonthlyReportState((prevState) => ({
                ...prevState,
                contractManageFreeEditor: value,
              }));
            }}
          />
          <RichTextEditor
            readonly={state.readonly}
            id={"Works Planned for Next Month"}
            title={"Works Planned for Next Month"}
            initialValue={monthlyReportState.worksPlanFreeEditor}
            onChange={(id, value) => {
              setMonthlyReportState((prevState) => ({
                ...prevState,
                worksPlanFreeEditor: value,
              }));
            }}
          />
          <RichTextEditor
            readonly={state.readonly}
            id={"Any Other Matter or Concerns"}
            title={"Any Other Matter or Concerns"}
            initialValue={monthlyReportState.anyOtherMatterFreeEditor}
            onChange={(id, value) => {
              setMonthlyReportState((prevState) => ({
                ...prevState,
                anyOtherMatterFreeEditor: value,
              }));
            }}
          />
          {monthlyReportState.newFreeEditorItems.map((item) => (
            <AddFreeEditorItem
              readonly={state.readonly}
              key={item.id}
              item={item}
              onTitleChange={(id, value) => {
                setMonthlyReportState((prevState) => ({
                  ...prevState,
                  newFreeEditorItems: prevState.newFreeEditorItems.map((i) => (i.id === id ? { ...item, title: value } : i)),
                }));
              }}
              onContentChange={(id, value) => {
                setMonthlyReportState((prevState) => ({
                  ...prevState,
                  newFreeEditorItems: prevState.newFreeEditorItems.map((i) => (i.id === id ? { ...i, content: value } : i)),
                }));
              }}
              onRemove={(id) => {
                setMonthlyReportState((prevState) => ({
                  ...prevState,
                  newFreeEditorItems: prevState.newFreeEditorItems.filter((item) => item.id !== id),
                }));
              }}
            />
          ))}
          {!state.readonly && <AddButtonWithLines onAdd={addNewMonthlyFreeEditor} />}
          <UploadPhoto
            readonly={state.readonly}
            title="Photos"
            images={monthlyReportState.uploadedImages}
            onDelete={(index) => {
              const updatedImages = monthlyReportState.uploadedImages.filter((_, i) => i !== index);
              setMonthlyReportState((prevState) => ({
                ...prevState,
                uploadedImages: updatedImages,
              }));
              URL.revokeObjectURL(monthlyReportState.uploadedImages[index].url);
            }}
            onSitePhotoClick={() => {
              setState((prevState) => ({
                ...prevState,
                sitePhotoPopup: true,
              }));
            }}
            onLocalFileChange={(event) => {
              monthlyImagesChange(event);
            }}
          />
          <ReportTextTitle content={"Signed By"} sx={{ display: "inline-block" }} />
          <ReportTextTitle
            content={monthlyReportState.signedby}
            sx={{
              fontSize: "12px",
              fontWeight: "normal",
              ml: 2,
              display: "inline-block",
            }}
          />
          <ReportTextTitle content={"Appendix 1"} />
          <ReportTextTitle content={"Schedule Gantt Chart"} sx={{ fontSize: "12px" }} />
          {!state.readonly ? (
            <Box ref={ganttChartRef} height={state.ganttChartHeight ? 390 : "auto"}>
              <ScheduleGantt
                tasks={state.taskTableData}
                startDate={state.startDate}
                endDate={state.endDate}
                currentDate={state.currentDate}
                SimplifiedVersion={true}
                defaultExpandAll={true}
                setCurrentDate={(d) =>
                  setState((prevState) => ({
                    ...prevState,
                    currentDate: d ? dayjs(d) : null,
                  }))
                }
                miniColumnWidth={22}
                treeTableHeaders={treeTableHeaders}
                selectedRowId={state.selectedGanttRowId}
                handleRowClick={(row) => {
                  setState((prevState) => ({
                    ...prevState,
                    selectedGanttRowId: row.taskId,
                  }));
                }}
              ></ScheduleGantt>
            </Box>
          ) : (
            <>
              {monthlyReportState.ganttChartImg && (
                <img style={{ width: "100%" }} src={monthlyReportState.ganttChartImg} alt="Gantt Chart" key="gantt-chart-img" />
              )}
            </>
          )}
          <ReportTextTitle content={"Appendix 2"} />
          <ReportTextTitle content={"3 Week Look-ahead Schedule"} sx={{ fontSize: "12px" }} />
          <UploadPDF
            readonly={state.readonly}
            onFileChange={(file) => {
              setMonthlyReportState((prevState) => ({
                ...prevState,
                uploadedPDF: file,
              }));
            }}
            uploadedPDF={monthlyReportState.uploadedPDF}
          />
          {monthlyReportState.newAppendixItems.map((item, index) => (
            <AddFreeEditorItem
              readonly={state.readonly}
              Appendix
              index={index}
              key={item.id}
              item={item}
              onTitleChange={(id, value) => {
                setMonthlyReportState((prevState) => ({
                  ...prevState,
                  newAppendixItems: prevState.newAppendixItems.map((i) => (i.id === id ? { ...item, title: value } : i)),
                }));
              }}
              onContentChange={(id, value) => {
                setMonthlyReportState((prevState) => ({
                  ...prevState,
                  newAppendixItems: prevState.newAppendixItems.map((i) => (i.id === id ? { ...i, content: value } : i)),
                }));
              }}
              onRemove={(id) => {
                setMonthlyReportState((prevState) => ({
                  ...prevState,
                  newAppendixItems: prevState.newAppendixItems.filter((item) => item.id !== id),
                }));
              }}
            />
          ))}
          {!state.readonly && <AddButtonWithLines onAdd={addNewMonthlyAppendix} />}
        </Box>
      </Box>
    );
  }, [
    state.reportName,
    state.selectedReportId,
    state.selectedRowData,
    state.ganttChartHeight,
    state.currentLockStatus,
    state.selectedGanttRowId,
    projectName,
    userWithCompany,
    monthlyReportState,
  ]);

  const renderReportDetails = useMemo(() => {
    if (state.isLoadingReportDetail) {
      return (
        <Box width="100%" height="calc(100% - 25px)" display="flex" flexDirection="column" gap={1} justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      );
    } else if (state.selectedReportId.length > 0) {
      switch (state.activeTab) {
        case "Daily Report":
          return renderDailyReport();
        case "Weekly Report":
          return renderWeeklyReport();
        case "Monthly Report":
          return renderMonthlyReport();
        default:
          return null;
      }
    } else {
      return (
        <Box width="100%" height="calc(100% - 25px)" display="flex" flexDirection="column" gap={1} justifyContent="center" alignItems="center">
          <img src="/assets/illustrations/report.svg" alt="report" style={{ width: 64, height: 64 }} />
          <Typography variant="h4" sx={{ color: "#717273", fontWeight: "bold" }}>
            Select a report to edit or review
          </Typography>
        </Box>
      );
    }
  }, [renderDailyReport, renderWeeklyReport, renderMonthlyReport, state.isLoadingReportDetail, state.selectedReportId, state.activeTab]);

  return (
    <>
      <Box width="100%" height="100%" display="flex" gap={2}>
        <Box sx={{ flex: "1 1 auto", height: "100%" }} display={state.layoutStyle ? "none" : "block"}>
          <Container title="Report Summary" icon="/assets/icons/report.svg" sx={{ height: "100%" }} overflow="hidden">
            <Box
              sx={{
                px: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "end",
                mt: -1,
              }}
            >
              <Box sx={{ minWidth: 350 }}>
                <ButtonGroup variant="text" color="primary">
                  {reportTab.map((tab, index) => {
                    return (
                      <Button
                        key={index}
                        onClick={() => handleTabClick(tab)}
                        style={{
                          color: "#717273",
                          border: "1px solid #D0D5DE",
                          height: "32px",
                          borderBottom: state.activeTab === tab ? "3px solid #33A3DB" : "1px solid #D0D5DE",
                          borderLeft: index === 0 ? "1px solid #D0D5DE" : "none",
                        }}
                      >
                        {tab}
                      </Button>
                    );
                  })}
                </ButtonGroup>
              </Box>
              <Box sx={{ maxWidth: 350, minWidth: 350 }}>
                <Typography variant="subtitle1" sx={{ color: "#333", fontWeight: 700, whiteSpace: "nowrap" }}>
                  Date Range
                </Typography>
                <Box display="flex" gap={0.5} alignItems="center" sx={{ my: 0.5 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label=""
                      value={reportFilterState.startDate}
                      onChange={(date) => handleDateChange("startDate", date)}
                      inputStyle={{ height: 26, py: 0 }}
                    />
                    <Typography>to</Typography>
                    <DatePicker
                      label=""
                      value={reportFilterState.endDate}
                      onChange={(date) => handleDateChange("endDate", date)}
                      inputStyle={{ height: 26, py: 0 }}
                    />
                  </LocalizationProvider>
                  <IconButton
                    aria-label="search"
                    onClick={() => handleSearchClick()}
                    sx={{
                      border: "1px solid #c6ccd2",
                      borderRadius: "4px",
                      padding: "2px 3px",
                      boxSizing: "border-box",
                    }}
                  >
                    <Search sx={{ fontSize: "20px" }} />
                  </IconButton>
                  <IconButton
                    aria-label="reset"
                    onClick={() => handleReset()}
                    sx={{
                      border: "1px solid #c6ccd2",
                      borderRadius: "4px",
                      padding: "2px 3px",
                      boxSizing: "border-box",
                    }}
                  >
                    <Refresh sx={{ fontSize: "20px" }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
            <Box sx={{ px: 2, mt: 2, height: "calc(100% - 104px)" }}>
              <MainTable
                paperSx={{
                  height: "100%",
                }}
                sx={{
                  maxHeight: "calc(100% - 52px)",
                  height: "calc(100% - 52px)",
                }}
                columns={state.reportTableHeaders}
                rows={state.reportTableData[state.activeTab]}
                iconColumns={iconColumns}
                headerStyles={{
                  default: { backgroundColor: "#F1F1F1" },
                }}
                columnWidths={{
                  icon: "50px",
                  projectReportId: "72px",
                  lastUpdatedBy: "110px",
                }}
                showPagination
                page={state.page}
                rowsPerPage={state.rowsPerPage}
                onPageChange={(newPage) => setState((prevState) => ({ ...prevState, page: newPage }))}
                onRowsPerPageChange={(newRowsPerPage) =>
                  setState((prevState) => ({
                    ...prevState,
                    rowsPerPage: newRowsPerPage,
                  }))
                }
                selectable="single"
                sortable
                sortableColumns={["preparedBy"]}
                showRadioInSingleSelect
                rowClickColor={"#EBFFFE"}
                selected={state.selectedReportId}
                setSelected={handleRowSelect}
                isLoading={state.isLoadingTable}
              />
            </Box>
          </Container>
        </Box>
        <Box sx={{ flex: "1 1 auto", height: "100%", minWidth: 800 }}>
          <Container title={state.reportTitle} icon="/assets/icons/report.svg" sx={{ height: "100%" }}>
            {renderReportDetails}
            {state.currentEditUser && (
              <Typography
                sx={{
                  position: "absolute",
                  left: "42px",
                  top: "42px",
                }}
              >
                {state.currentEditUser} is currently editing the report
              </Typography>
            )}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: "30px",
                cursor: "pointer",
                transform: "rotate(180deg)",
              }}
              onClick={() => {
                setState((prevState) => ({
                  ...prevState,
                  layoutStyle: !state.layoutStyle,
                }));
              }}
            >
              <img
                src={state.layoutStyle ? "/assets/buttons/collapse.svg" : "/assets/buttons/expansion.svg"}
                alt="expansion"
                style={{ width: "30px", height: "30px" }}
              />
            </Box>
          </Container>
        </Box>
      </Box>
      {/* sitephoto popup*/}
      <Popup
        open={state.sitePhotoPopup}
        onClose={() => {
          setState((prevState) => ({
            ...prevState,
            sitePhotoPopup: false,
          }));
        }}
        popupStyle={{
          minWidth: 1400,
          maxWidth: 1400,
        }}
      >
        <SitePhotoPage
          isPopup
          imgInforValue={imgFromSitePhoto}
          closePopup={() => {
            setState((prevState) => ({
              ...prevState,
              sitePhotoPopup: false,
            }));
          }}
        />
      </Popup>
      {/* reject reason popup */}
      <Popup
        open={state.rejectReasonPopup}
        onClose={() => {
          setState((prevState) => ({
            ...prevState,
            rejectReasonPopup: false,
          }));
        }}
        popupStyle={{
          minWidth: 800,
          maxWidth: 800,
        }}
        title="Rejection/Retraction Reason"
      >
        <Box maxHeight={800} overflow="auto">
          <Box sx={{ mx: 4, mb: 4 }}>
            <Typography variant="subtitle1" sx={{ my: 2 }}>
              Report Selected: <b>{state.rejectPopupName}</b>
            </Typography>
            <MainTable
              sx={{ maxHeight: "calc(100% - 230px)" }}
              columns={state.rejectReasonTableHeaders}
              rows={state.rejectReasonTableData}
              headerStyles={{
                default: { backgroundColor: "#F1F1F1" },
              }}
              sortable
              sortableColumns={["datetime"]}
              rowClickColor={"#EBFFFE"}
              isLoading={state.isLoadingTable}
            />
          </Box>
        </Box>
      </Popup>
      {/* reject popup */}
      <Popup
        open={state.rejectPopup}
        onClose={() => {
          setState((prevState) => ({
            ...prevState,
            rejectPopup: false,
          }));
        }}
        popupStyle={{
          minWidth: 500,
          maxWidth: 500,
        }}
        title="Notification"
        variant="error"
      >
        <Box maxHeight={800} overflow="auto">
          <Box sx={{ mx: 4, my: 2, textAlign: "center" }}>
            <Typography variant="subtitle1">Are you sure to reject the following report?</Typography>
            <Typography variant="subtitle1">
              Report Name: <b>{state.reportName}</b>
            </Typography>
            <Typography variant="subtitle1">Please enter a rejection reason then select “Yes” to proceed</Typography>
            <TextField
              value={state.rejectionReason}
              onChange={(event) =>
                setState((prevState) => ({
                  ...prevState,
                  rejectionReason: event.target.value,
                }))
              }
              variant="outlined"
              placeholder="Please select rejection reason in 50 characters..."
              sx={{
                width: "340px",
                mt: 1,
                "& .MuiInputBase-input": { px: 1, py: 0.5 },
              }}
            />
          </Box>
        </Box>
        <Box textAlign="center">
          <Button onClick={confirmReject} variant="outlined" color="inherit" sx={{ borderRadius: "10px", lineHeight: "normal", mb: 2 }}>
            Yes
          </Button>
          <Button variant="outlined" color="inherit" sx={{ borderRadius: "10px", lineHeight: "normal", mb: 2, ml: 2 }}>
            No
          </Button>
        </Box>
      </Popup>
      {/* retract popup */}
      <Popup
        open={state.retractPopup}
        onClose={() => {
          setState((prevState) => ({
            ...prevState,
            retractPopup: false,
          }));
        }}
        popupStyle={{
          minWidth: 500,
          maxWidth: 500,
        }}
        title="Notification"
        variant="error"
      >
        <Box maxHeight={800} overflow="auto">
          <Box sx={{ mx: 4, my: 2, textAlign: "center" }}>
            <Typography variant="subtitle1">Are you sure to retract the following report?</Typography>
            <Typography variant="subtitle1">
              Report Name: <b>{state.reportName}</b>
            </Typography>
            <Typography variant="subtitle1">Please enter a retract reason then select “Yes” to proceed</Typography>
            <TextField
              value={state.retractReason}
              onChange={(event) =>
                setState((prevState) => ({
                  ...prevState,
                  retractReason: event.target.value,
                }))
              }
              variant="outlined"
              placeholder="Please select retract reason in 50 characters..."
              sx={{
                width: "340px",
                mt: 1,
                "& .MuiInputBase-input": { px: 1, py: 0.5 },
              }}
            />
          </Box>
        </Box>
        <Box textAlign="center">
          <Button onClick={() => confirmRetract("PM")} variant="outlined" color="inherit" sx={{ borderRadius: "10px", lineHeight: "normal", mb: 2 }}>
            Yes
          </Button>
          <Button variant="outlined" color="inherit" sx={{ borderRadius: "10px", lineHeight: "normal", mb: 2, ml: 2 }}>
            No
          </Button>
        </Box>
      </Popup>
      <NotificationPopup
        variant="secondary"
        title={state.confirmPopupTitle}
        open={state.isConfirming}
        message={state.confirmPopupText}
        proceedButtonLabel="Confirm"
        onClose={() => {
          setState((prevState) => ({
            ...prevState,
            isConfirming: false,
          }));
        }}
        onProceed={() => {
          if (state.actionType === "saveEditReport") {
            confirmSaveEditOrComplete("");
          } else if (state.actionType === "submitReport") {
            confirmSubmit();
          } else if (state.actionType === "completeReport") {
            confirmSaveEditOrComplete("complete");
          } else if (state.actionType === "rejectReport") {
            confirmReject();
          } else if (state.actionType === "retractReport") {
            confirmRetract("user");
          }
        }}
      ></NotificationPopup>
      <FullScreenLoading isLoading={state.isLoadingFullScreen} />
    </>
  );
};

export default ReportPage;
